import { useState, useEffect, useRef, useMemo, useCallback, useDeferredValue } from "react";
import { createClient } from "@supabase/supabase-js";
import {
  SUPABASE_URL,
  SUPABASE_ANON_KEY,
  USER_PRIVATE_DATA_TABLE,
  USER_PROMPTS_TABLE,
  ACCESS_REQUESTS_TABLE,
  ACTIVE_SESSIONS_TABLE,
  SINGLE_SESSION_ENFORCED,
  STARTER_USER_ID,
  DEMO_EMAILS,
  DEFAULT_AI_PROVIDER,
  OPENAI_MODEL,
  ANTHROPIC_MODEL,
  AI_BEURTEILUNG_ENDPOINT,
  SYSTEM_PROMPT,
  DARK,
  LIGHT,
} from "./constants/appConstants";
import { syncLabeledLine } from "./utils/befundHelpers";
import { appendRichText, copyRichTextToClipboard, richTextToPlainText } from "./utils/richTextHelpers";
import { formatSupabaseError, deriveChangedRange } from "./utils/supabaseHelpers";
import { buildRadiologyContext, buildAiAssistantContext as buildAiAssistantContextValue } from "./utils/aiContextHelpers";
import { resolveCloudSessionState } from "./utils/sessionStateHelpers";
import { EMPTY_BEFUNDBEISPIELE, createEmptyBefundbeispiele, createEmptyProtokolle, findCustomPrompt, isMrtVariant, mapBefundbeispieleRows } from "./utils/befundbeispieleHelpers";
import {
  ensureModalitaetenVorlagen,
  getVorlageActualRegion,
  getVorlageCategory,
  getVorlageDisplayName,
  getVorlageSubsection,
  mapBaselineVorlageRow,
  resolvePromptTargetMod,
} from "./utils/vorlagenHelpers";
import {
  createNewVorlageEntry,
  normalizeVorlageForSave,
  validateNewVorlageInput,
} from "./utils/vorlagenCrudHelpers";
import useGroupOrder from "./hooks/useGroupOrder";
import useGroupManagement from "./hooks/useGroupManagement";
import useExternalResources from "./hooks/useExternalResources";
import useVorlagenOperations from "./hooks/useVorlagenOperations";
import useBefundbeispieleOperations from "./hooks/useBefundbeispieleOperations";
import useAiWorkflowOperations from "./hooks/useAiWorkflowOperations";
import useEditorFieldHistory from "./hooks/useEditorFieldHistory";
import useReportHistory from "./hooks/useReportHistory";
import useFieldSelectors from "./hooks/useFieldSelectors";
import useAccessManagement from "./hooks/useAccessManagement";
import useModalityDisplay from "./hooks/useModalityDisplay";
import InlineDropdown from "./components/ui/InlineDropdown";
import { ActionButton, DropdownItem } from "./components/ui/Buttons";
import EditorTab from "./components/tabs/EditorTab";
import BefundbeispieleTab from "./components/tabs/BefundbeispieleTab";
import VorlagenTab from "./components/tabs/VorlagenTab";
import ResourcesTab from "./components/tabs/ResourcesTab";
import AccessRequestsTab from "./components/tabs/AccessRequestsTab";
import CodeConfirmModal from "./components/modals/CodeConfirmModal";
import LoginPage from "./components/auth/LoginPage";
import AppHeader from "./components/AppHeader";
import { createUserPrivateDataService } from "./services/userPrivateDataService";
import { createAccessControlService } from "./services/accessControlService";
import { createAiService } from "./services/aiService";
import { createTableNameService } from "./services/tableNameService";
import { createUserPromptsService } from "./services/userPromptsService";
import { createVorlagenService } from "./services/vorlagenService";

const supabase = SUPABASE_URL && SUPABASE_ANON_KEY
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY)
  : null;

const HISTORY_STORAGE_KEY = "radventura.reportHistory.v1";
const AUTOSAVE_ENABLED_STORAGE_KEY = "radventura.autosaveEnabled.v1";
const AI_BEFUND_HISTORY_STORAGE_KEY = "radventura.aiBefundHistory.v1";
const AI_PROVIDER_STORAGE_KEY = "radventura.aiProvider.v1";
const BEURTEILUNG_GENERATE_PROMPT_STORAGE_KEY = "radventura.beurteilungGeneratePrompt.v1";
const AI_BEURTEILUNG_STYLE_PROFILE_STORAGE_KEY = "radventura.aiBeurteilungStyleProfile.v1";
const USER_VORLAGEN_STORAGE_KEY = "radventura.userVorlagen.v1";
const USER_BEFUNDBEISPIELE_STORAGE_KEY = "radventura.userBefundbeispiele.v1";
const USER_PROTOKOLLE_STORAGE_KEY = "radventura.userProtokolle.v1";
const USER_VORLAGEN_GROUPS_STORAGE_KEY = "radventura.userVorlagenGroups.v1";
const USER_BEFUNDBEISPIELE_GROUPS_STORAGE_KEY = "radventura.userBefundGroups.v1";
const USER_PROTOKOLLE_GROUPS_STORAGE_KEY = "radventura.userProtokollGroups.v1";
const USER_VORLAGEN_GROUP_ORDER_STORAGE_KEY = "radventura.userVorlagenGroupOrder.v1";
const USER_BEFUNDBEISPIELE_GROUP_ORDER_STORAGE_KEY = "radventura.userBefundGroupOrder.v1";
const USER_PROTOKOLLE_GROUP_ORDER_STORAGE_KEY = "radventura.userProtokollGroupOrder.v1";
const USER_VORLAGEN_SUBSECTION_ORDER_STORAGE_KEY = "radventura.userVorlagenSubsectionOrder.v1";
const USER_VORLAGEN_CATEGORY_STORAGE_KEY = "radventura.userVorlagenCategory.v1";
const USER_VORLAGEN_EMPTY_CATEGORIES_STORAGE_KEY = "radventura.userVorlagenEmptyCategories.v1";
const USER_LIBRARY_STORAGE_KEY = "radventura.userLibrary.v1";
const USER_AI_QUICK_PROMPTS_STORAGE_KEY = "radventura.userAiQuickPrompts.v1";
const USER_AI_BEURTEILUNG_QUICK_PROMPTS_STORAGE_KEY = "radventura.userAiBeurteilungQuickPrompts.v1";
const DEFAULT_VORLAGEN_GROUPS = ["CT", "MRT"];
const DEFAULT_VORLAGEN_CREATE_GROUPS = ["CT", "MRT"];
const INTERNAL_VORLAGEN_GROUPS = ["Modalitäten"];
const HIDDEN_VORLAGEN_GROUPS = ["Volumetrie"];
const DEFAULT_BEFUNDBEISPIELE_GROUPS = [];
const DEFAULT_PROTOKOLLE_GROUPS = [];
const USER_DATA_KEY_VORLAGEN = "vorlagen";
const USER_DATA_KEY_BEFUNDBEISPIELE = "befundbeispiele";
const USER_DATA_KEY_PROTOKOLLE = "protokolle";
const USER_DATA_KEY_LIBRARY = "library";
const USER_DATA_KEY_AI_QUICK_PROMPTS = "ai_quick_prompts";
const USER_DATA_KEY_AI_BEURTEILUNG_QUICK_PROMPTS = "ai_beurteilung_quick_prompts";
const USER_DATA_KEY_BEURTEILUNG_GENERATE_PROMPT = "beurteilung_generate_prompt";
const USER_DATA_KEY_AI_BEURTEILUNG_STYLE_PROFILE = "ai_beurteilung_style_profile";
const USER_DATA_KEY_EDITOR_DRAFT = "editor_draft";
const USER_DATA_KEY_REPORT_HISTORY = "report_history";
const USER_DATA_KEY_AI_BEFUND_HISTORY = "ai_befund_history";
const USER_DATA_KEY_AUTOSAVE_ENABLED = "autosave_enabled";
const USER_DATA_KEY_AI_PROVIDER = "ai_provider";
const USER_DATA_KEY_DARK_MODE = "dark_mode";
const USER_DATA_KEY_ONBOARDING_SEEN = "onboarding_seen";
const USER_DATA_KEY_CUSTOM_VORLAGEN_GROUPS = "custom_vorlagen_groups";
const USER_DATA_KEY_CUSTOM_BEFUND_GROUPS = "custom_befund_groups";
const USER_DATA_KEY_CUSTOM_PROTOKOLL_GROUPS = "custom_protokoll_groups";
const USER_DATA_KEY_VORLAGEN_GROUP_ORDER = "vorlagen_group_order";
const USER_DATA_KEY_BEFUND_GROUP_ORDER = "befund_group_order";
const USER_DATA_KEY_PROTOKOLL_GROUP_ORDER = "protokoll_group_order";
const USER_DATA_KEY_VORLAGEN_SUBSECTION_ORDER = "vorlagen_subsection_order";
const USER_DATA_KEY_VORLAGEN_CATEGORY = "vorlagen_category";
const USER_DATA_KEY_VORLAGEN_EMPTY_CATEGORIES = "vorlagen_empty_categories";
const AI_BEFUND_HISTORY_LIMIT = 5;
const AI_QUICK_PROMPTS_LIMIT = 12;
const APP_TABS = [
  { key: "befundung", label: "Befundung", adminOnly: false },
  { key: "vorlagen", label: "Vorlagen", adminOnly: false },
  { key: "protokolle", label: "Protokolle", adminOnly: false },
  { key: "befundbeispiele", label: "Befundbeispiele", adminOnly: false },
  { key: "bibliothek", label: "Bibliothek", adminOnly: false },
  { key: "freigaben", label: "Freigaben", adminOnly: true },
];
const AI_ERROR_UNKNOWN = "Unbekannt";
const AI_MSG_ENTER_BEFUND_FIRST = "Bitte zuerst einen Befund eingeben.";
const AI_MSG_ENTER_PROMPT_FIRST = "Bitte eine Anweisung für den AI-Assistenten eingeben.";
const AI_MSG_SELECT_BEFUND_FIRST = "Bitte zuerst einen Abschnitt im Befund markieren.";
const AI_MSG_NO_RESPONSE = "Keine AI-Antwort erhalten.";
const AI_MSG_HISTORY_ENTRY_LOADED = "AI-Verlaufseintrag geladen ✓";
const AI_MSG_HISTORY_ENTRY_REMOVED = "AI-Verlaufseintrag entfernt ✓";
const AI_MSG_HISTORY_CLEARED = "AI-Verlauf gelöscht ✓";
const AI_MSG_PROMPT_SAVED = "AI-Prompt gespeichert ✓";
const AI_MSG_PROMPT_DELETED = "AI-Prompt gelöscht ✓";
const AI_MSG_ENTER_BEURTEILUNG_FIRST = "Bitte zuerst eine Beurteilung eingeben.";
const AI_MSG_BEURTEILUNG_PROMPT_SAVED = "AI-Beurteilung-Prompt gespeichert ✓";
const AI_MSG_BEURTEILUNG_PROMPT_DELETED = "AI-Beurteilung-Prompt gelöscht ✓";
const DEFAULT_BEURTEILUNG_GENERATE_PROMPT = "Erstelle eine präzise radiologische Beurteilung auf Basis des Befunds, beantworte die Fragestellung direkt und bleibe fachlich klar. Gib nur den finalen Beurteilungstext aus.";
const BEURTEILUNG_STYLE_PROFILES = ["ultrakurz", "standard", "detailliert"];
const DEFAULT_BEURTEILUNG_STYLE_PROFILE = "standard";
const AI_SELECTION_ONLY_INSTRUCTION = "Wichtig: Überarbeite ausschließlich den markierten Abschnitt und antworte nur mit der überarbeiteten Fassung dieses Abschnitts.";
const AI_FULL_BEFUND_INSTRUCTION = "Wichtig: Überarbeite den gesamten Befund und antworte nur mit der vollständigen überarbeiteten Fassung des Befunds.";
const AI_USER_INSTRUCTION_LABEL = "Benutzeranweisung:";
const DEFAULT_MODALITAETEN_VORLAGEN = [
  {
    id: "modalitaeten-ct",
    name: "CT",
    mod: "Modalitäten",
    befund: "",
    beurteilung: null,
    created_at: 1,
  },
  {
    id: "modalitaeten-mrt-15",
    name: "MRT 1,5",
    mod: "Modalitäten",
    befund: "",
    beurteilung: null,
    created_at: 2,
  },
  {
    id: "modalitaeten-mrt-3",
    name: "MRT 3",
    mod: "Modalitäten",
    befund: "",
    beurteilung: null,
    created_at: 3,
  },
];
const DEFAULT_AI_QUICK_PROMPTS = [
  { id: "quick-1", title: "Sprachlich straffen", text: "Straffe den Befund sprachlich, ohne Inhalte zu verlieren." },
  { id: "quick-2", title: "Fachsprachlich präzisieren", text: "Formuliere den Befund fachsprachlich präziser." },
  { id: "quick-3", title: "Struktur und Grammatik", text: "Verbessere Struktur, Stil und Grammatik des Befunds." },
  { id: "quick-4", title: "Redundanzen reduzieren", text: "Überarbeite nur unklare oder redundante Passagen und erhalte die Aussage." },
  { id: "quick-5", title: "Volumetrie Prompt", text: "Wenn Volumetrie-Inhalte vorliegen, berücksichtige sie prioritär und formuliere ausschließlich den Befund konsistent.", target: "assistant" },
];
const DEFAULT_AI_BEURTEILUNG_QUICK_PROMPTS = [
  { id: "beurteilung-quick-1", title: "Kürzer formulieren", text: "Kürze die Beurteilung auf 2-3 präzise Sätze, ohne klinischen Informationsverlust." },
  { id: "beurteilung-quick-2", title: "Diagnosefokus schärfen", text: "Schärfe die wahrscheinlichste Diagnose und formuliere Differentialdiagnosen klarer." },
  { id: "beurteilung-quick-3", title: "Fragestellung direkt beantworten", text: "Formuliere die Beurteilung so, dass die Fragestellung direkt und eindeutig beantwortet wird." },
  { id: "beurteilung-quick-4", title: "Empfehlung ergänzen", text: "Ergänze bei Bedarf eine kurze, sinnvolle Empfehlung für weiteres Procedere." },
];
const AI_BEFUND_ASSISTANT_SYSTEM_PROMPT = `Du bist ein präziser radiologischer Schreibassistent für deutschsprachige Befunde.

Deine Aufgabe ist es, einen bestehenden radiologischen Befund anhand einer konkreten Benutzeranweisung zu überarbeiten.

Regeln:
- Antworte ausschließlich mit dem überarbeiteten Befundtext
- Keine Einleitung, keine Erklärung, keine Aufzählung der Änderungen
- Erhalte medizinische Präzision und radiologischen Stil
- Ändere nur das, was durch die Benutzeranweisung verlangt ist
- Wenn die Anweisung eine Kürzung verlangt, kürze ohne Informationsverlust
- Wenn die Anweisung eine Umformulierung verlangt, bleibe fachsprachlich sauber
- Erfinde keine klinischen Fakten, die nicht im Kontext enthalten sind
- Gib niemals Markdown oder Anführungszeichen um den Text aus`;

const createTimeBasedId = () => Date.now().toString();
const normalizeAiQuickPrompts = (list, idPrefix = "quick-custom") => {
  if (!Array.isArray(list)) return [];
  const mapped = list.map((entry) => {
    const title = (entry?.title || "").toString();
    const text = (entry?.text || "").toString();
    const storedTarget = entry?.target === "assistant" ? "assistant" : entry?.target === "report" ? "report" : "";
    const isVolumetrie = /volumetrie/i.test(title) || /volumetrie/i.test(text);
    
    // Osiguraj stabilan ID čak i za starije prompte bez ID-a
    let id = entry?.id;
    if (!id) {
      // Generiši stabilan ID na osnovu hash-a naslova i teksta
      const contentHash = `${title}-${text}`.split('').reduce((h, c) => ((h << 5) - h) + c.charCodeAt(0), 0).toString(36);
      id = `${idPrefix}-${contentHash}`;
    }
    
    return {
      id,
      title,
      text,
      target: storedTarget || (isVolumetrie ? "assistant" : "report"),
    };
  });
  const filtered = mapped.filter((entry) => entry.text.trim() !== "");
  return filtered;
};
const createUserScopedStorageKey = (prefix, userId) => `${prefix}.${userId || "anonymous"}`;
const inMemoryStorage = new Map();
const readJsonStorage = (key, fallbackValue) => {
  if (!inMemoryStorage.has(key)) {
    return fallbackValue;
  }
  return inMemoryStorage.get(key);
};
const writeJsonStorage = (key, value) => {
  inMemoryStorage.set(key, value);
};

const normalizeGroupName = (value) => String(value || "").trim();

const dedupeGroupNames = (values) => {
  const seen = new Set();
  const result = [];

  for (const value of values || []) {
    const normalized = normalizeGroupName(value);
    if (!normalized) continue;

    const key = normalized.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(normalized);
  }

  return result;
};

const normalizeOrderMap = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.entries(value).reduce((acc, [key, order]) => {
    const groupName = normalizeGroupName(key);
    if (!groupName || !Array.isArray(order)) return acc;
    acc[groupName] = dedupeGroupNames(order);
    return acc;
  }, {});
};

const normalizeStringArrayMap = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.entries(value).reduce((acc, [key, values]) => {
    const mapKey = String(key || "").trim();
    if (!mapKey || !Array.isArray(values)) return acc;
    acc[mapKey] = dedupeGroupNames(values);
    return acc;
  }, {});
};

const createVorlageCategoryKey = (vorlage) => {
  const mod = String(vorlage?.mod || "").trim();
  const name = String(vorlage?.name || "").trim();
  if (!mod || !name) return "";
  return `stable:${mod}:${name}`;
};

const createVorlageAssignmentCategoryKey = (vorlage) => {
  const mod = String(vorlage?.mod || "").trim();
  const subsection = getVorlageSubsection(vorlage) || getVorlageDisplayName(vorlage);
  const displayName = getVorlageDisplayName(vorlage);
  if (!mod || !subsection || !displayName) return "";
  return `assignment:${mod}:${subsection}:${displayName}`;
};

const getVorlageCategoryValue = (categoryMap, vorlage) => {
  if (!vorlage) return "";
  return (
    vorlage?.category ||
    getVorlageCategory(vorlage) ||
    categoryMap?.[vorlage.id] ||
    categoryMap?.[createVorlageCategoryKey(vorlage)] ||
    categoryMap?.[createVorlageAssignmentCategoryKey(vorlage)] ||
    ""
  );
};

const mergeVorlagenCategoryOverlay = (baseVorlagen, overlayVorlagen) => {
  if (!Array.isArray(baseVorlagen) || !Array.isArray(overlayVorlagen)) return baseVorlagen;
  const categoryByKey = overlayVorlagen.reduce((acc, vorlage) => {
    const category = String(vorlage?.category || "").trim();
    if (!category) return acc;
    if (vorlage?.id) acc[String(vorlage.id)] = category;
    const stableKey = createVorlageCategoryKey(vorlage);
    if (stableKey) acc[stableKey] = category;
    const assignmentKey = createVorlageAssignmentCategoryKey(vorlage);
    if (assignmentKey) acc[assignmentKey] = category;
    return acc;
  }, {});

  return baseVorlagen.map((vorlage) => {
    const category = getVorlageCategoryValue(categoryByKey, vorlage);
    return category ? { ...vorlage, category } : vorlage;
  });
};

const countSentences = (value) => (
  String(value || "")
    .split(/[.!?]+/)
    .map((entry) => entry.trim())
    .filter(Boolean)
    .length
);

const countNegativeMarkers = (value) => {
  const matches = String(value || "").match(/(^|[\s,;])(kein|keine|keinen|keinem|keiner|ohne)\b/gi);
  return matches ? matches.length : 0;
};

const normalizeBeurteilungStyleProfile = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return BEURTEILUNG_STYLE_PROFILES.includes(normalized)
    ? normalized
    : DEFAULT_BEURTEILUNG_STYLE_PROFILE;
};

const getBeurteilungStyleRuleSet = (profile) => {
  if (profile === "ultrakurz") {
    return { maxSentences: 1, maxChars: 170, compressChars: 180, compressSentences: 2, compressNegativeMarkers: 2, compressCommas: 4 };
  }
  if (profile === "detailliert") {
    return { maxSentences: 4, maxChars: 420, compressChars: 440, compressSentences: 5, compressNegativeMarkers: 6, compressCommas: 10 };
  }
  return { maxSentences: 2, maxChars: 260, compressChars: 260, compressSentences: 3, compressNegativeMarkers: 3, compressCommas: 6 };
};

const buildBeurteilungStyleInstruction = (profile) => {
  if (profile === "ultrakurz") {
    return "Stilprofil: Ultrakurz. Maximal 1 Satz, nur die klinisch wegweisende Kernaussage.";
  }
  if (profile === "detailliert") {
    return "Stilprofil: Detailliert. Bis zu 4 kurze Sätze, dennoch fokussiert und ohne redundante Negativlisten.";
  }
  return "Stilprofil: Standard. Maximal 2 prägnante Sätze, direkt auf die Fragestellung fokussiert.";
};

const buildBeurteilungCompressorSystemPrompt = (profile) => {
  const styleInstruction = buildBeurteilungStyleInstruction(profile);
  return `Du bist ein strenger Qualitätsprüfer für radiologische Beurteilungen in allen Körperregionen.

Ziel: klinisch wegweisend, ohne unnötige Breite.

${styleInstruction}

Regeln:
- Entferne alles, was die klinische Entscheidung nicht verändert.
- Keine Aufzählung multipler unauffälliger Negativbefunde.
- Wenn kein weiterer relevanter Befund vorliegt, nutze als Sammelsatz: "Sonst kein wegweisender Befund."
- Keine neuen Inhalte hinzufügen, keine Spekulation.
- Antworte nur mit der finalen Beurteilung, ohne Präfixe oder Erklärungen.`;
};

const shouldCompressBeurteilung = (value, profile = DEFAULT_BEURTEILUNG_STYLE_PROFILE) => {
  const text = String(value || "").trim();
  if (!text) return false;

  const normalizedProfile = normalizeBeurteilungStyleProfile(profile);
  const rules = getBeurteilungStyleRuleSet(normalizedProfile);
  const sentenceCount = countSentences(text);
  const negativeMarkers = countNegativeMarkers(text);
  const commaCount = (text.match(/,/g) || []).length;

  return (
    text.length > rules.compressChars
    || sentenceCount >= rules.compressSentences
    || negativeMarkers >= rules.compressNegativeMarkers
    || commaCount >= rules.compressCommas
  );
};

const sortGroupNames = (values, pinnedGroups = []) => {
  const uniqueGroups = dedupeGroupNames(values);
  const pinnedIndex = new Map(
    dedupeGroupNames(pinnedGroups).map((group, index) => [group.toLowerCase(), index])
  );

  return uniqueGroups.sort((left, right) => {
    const leftPinnedIndex = pinnedIndex.get(left.toLowerCase());
    const rightPinnedIndex = pinnedIndex.get(right.toLowerCase());
    const leftIsPinned = leftPinnedIndex != null;
    const rightIsPinned = rightPinnedIndex != null;

    if (leftIsPinned && rightIsPinned) return leftPinnedIndex - rightPinnedIndex;
    if (leftIsPinned) return -1;
    if (rightIsPinned) return 1;

    return left.localeCompare(right, "de", { numeric: true, sensitivity: "base" });
  });
};

const findExistingGroupName = (groups, candidate) => {
  const normalizedCandidate = normalizeGroupName(candidate).toLowerCase();
  if (!normalizedCandidate) return "";

  return dedupeGroupNames(groups).find((group) => group.toLowerCase() === normalizedCandidate) || "";
};

const decodeBase64Url = (value) => {
  const normalized = String(value || "").replace(/-/g, "+").replace(/_/g, "/");
  const padding = normalized.length % 4;
  const padded = padding ? normalized + "=".repeat(4 - padding) : normalized;
  try {
    return atob(padded);
  } catch {
    return "";
  }
};

const extractBefundOnly = (text) => {
  const raw = String(text || "").trim();
  if (!raw) return "";

  const beurteilungIndex = raw.search(/\n\s*beurteilung\s*:/i);
  if (beurteilungIndex === -1) return raw;

  return raw.slice(0, beurteilungIndex).trimEnd();
};

const getSessionIdFromAccessToken = (accessToken) => {
  const token = String(accessToken || "");
  const parts = token.split(".");
  if (parts.length < 2) return "";

  const json = decodeBase64Url(parts[1]);
  if (!json) return "";

  try {
    const payload = JSON.parse(json);
    return String(payload?.session_id || payload?.sid || "");
  } catch {
    return "";
  }
};

const isMissingSessionTableError = (error) => {
  const code = String(error?.code || "").toUpperCase();
  const message = String(error?.message || "").toLowerCase();
  const details = String(error?.details || "").toLowerCase();
  return (
    code === "42P01" ||
    code === "PGRST205" ||
    message.includes("user_active_sessions") ||
    details.includes("user_active_sessions")
  );
};

const isMissingRelationError = (error) => {
  const code = String(error?.code || "").toUpperCase();
  return code === "42P01" || code === "PGRST205";
};

const cloneData = (value) => {
  if (typeof structuredClone === "function") return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
};

export default function RadiologyApp() {
  const [isDarkTheme, setIsDarkTheme] = useState(true);
  const [fields, setFields] = useState({
    klinischeAngaben: "", fragestellung: "", modalitat: "", region: "", geschlecht: "", befund: "", beurteilung: "",
  });
  const [isModalityDropdownOpen, setIsModalityDropdownOpen] = useState(false);
  const [isRegionDropdownOpen, setIsRegionDropdownOpen] = useState(false);
  const [isGenderDropdownOpen, setIsGenderDropdownOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAiBefundLoading, setIsAiBefundLoading] = useState(false);
  const [isAiBeurteilungLoading, setIsAiBeurteilungLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("befundung");
  const [befundbeispiele, setBefundbeispiele] = useState(() => createEmptyBefundbeispiele());
  const [protokolle, setProtokolle] = useState(() => createEmptyProtokolle());
  const [templateDraftName, setTemplateDraftName] = useState("");
  const [templateDraftSection, setTemplateDraftSection] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [newSectionName, setNewSectionName] = useState("");
  const [newSectionMod, setNewSectionMod] = useState("CT");
  const [expandedSections, setExpandedSections] = useState({});
  const [filterText, setFilterText] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, id: null, modGroup: null, section: null });
  const [sectionDeleteConfirm, setSectionDeleteConfirm] = useState({ show: false, modGroup: null, section: null });
  const [newTplFormOpen, setNewTplFormOpen] = useState(false);
  const [newTplMod, setNewTplMod] = useState("CT");
  const [newTplSection, setNewTplSection] = useState("");
  const [newTplCategory, setNewTplCategory] = useState("");
  const [newTplName, setNewTplName] = useState("");
  const [newTplBefund, setNewTplBefund] = useState("");
  const [newTplBeurteilung, setNewTplBeurteilung] = useState("");
  const [protokollExpandedSections, setProtokollExpandedSections] = useState({});
  const [protokollFilterText, setProtokollFilterText] = useState("");
  const [newProtokollSectionName, setNewProtokollSectionName] = useState("");
  const [newProtokollSectionMod, setNewProtokollSectionMod] = useState("CT");
  const [newProtokollFormOpen, setNewProtokollFormOpen] = useState(false);
  const [newProtokollMod, setNewProtokollMod] = useState("CT");
  const [newProtokollSection, setNewProtokollSection] = useState("");
  const [newProtokollCategory, setNewProtokollCategory] = useState("");
  const [newProtokollName, setNewProtokollName] = useState("");
  const [newProtokollBefund, setNewProtokollBefund] = useState("");
  const [newProtokollBeurteilung, setNewProtokollBeurteilung] = useState("");
  const [protokollDeleteConfirm, setProtokollDeleteConfirm] = useState({ show: false, id: null, modGroup: null, section: null });
  const [protokollSectionDeleteConfirm, setProtokollSectionDeleteConfirm] = useState({ show: false, modGroup: null, section: null });
  const [reportHistory, setReportHistory] = useState([]);
  const [aiProvider, setAiProvider] = useState(DEFAULT_AI_PROVIDER);
  const [aiBefundPrompt, setAiBefundPrompt] = useState("");
  const [aiBefundResponse, setAiBefundResponse] = useState("");
  const [aiBefundHistory, setAiBefundHistory] = useState([]);
  const [aiQuickPrompts, setAiQuickPrompts] = useState(DEFAULT_AI_QUICK_PROMPTS);
  const [aiBeurteilungQuickPrompts, setAiBeurteilungQuickPrompts] = useState(DEFAULT_AI_BEURTEILUNG_QUICK_PROMPTS);
  const [aiPromptSyncStatus, setAiPromptSyncStatus] = useState(supabase ? "checking" : "local");
  const [aiBeurteilungPrompt, setAiBeurteilungPrompt] = useState("");
  const [beurteilungGeneratePrompt, setBeurteilungGeneratePrompt] = useState(DEFAULT_BEURTEILUNG_GENERATE_PROMPT);
  const [beurteilungStyleProfile, setBeurteilungStyleProfile] = useState(DEFAULT_BEURTEILUNG_STYLE_PROFILE);
  const [aiBefundLastAction, setAiBefundLastAction] = useState(null);
  const [, setAiBefundHighlightRange] = useState({ start: null, end: null, nonce: 0 });
  const [aiBefundSelection, setAiBefundSelection] = useState({ start: null, end: null, text: "" });
  const [lastAutosaveAt, setLastAutosaveAt] = useState("");
  const [autosaveEnabled, setAutosaveEnabled] = useState(false);
  const [vorlagen, setVorlagen] = useState([]);
  const [newPromptFormOpen, setNewPromptFormOpen] = useState(false);
  const [newPromptMod, setNewPromptMod] = useState("CT");
  const [newPromptMrtTarget, setNewPromptMrtTarget] = useState("MRT");
  const [newPromptSubsection, setNewPromptSubsection] = useState("");
  const [newPromptCategory, setNewPromptCategory] = useState("");
  const [newPromptName, setNewPromptName] = useState("");
  const [newPromptBefund, setNewPromptBefund] = useState("");
  const [newPromptBeurteilung, setNewPromptBeurteilung] = useState("");
  const [promptDeleteConfirm, setPromptDeleteConfirm] = useState({ show: false, id: null });
  const [session, setSession] = useState(null);
  const [accessStatus, setAccessStatus] = useState("idle");
  const [accessRecord, setAccessRecord] = useState(null);
  const [accessRequests, setAccessRequests] = useState([]);
  const [isAccessRequestsLoading, setIsAccessRequestsLoading] = useState(false);
  const [isAccessControlAvailable, setIsAccessControlAvailable] = useState(true);
  const [isAccessAdmin, setIsAccessAdmin] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [vorlagenFilterText, setVorlagenFilterText] = useState("");
  const [expandedVorlagenGroups, setExpandedVorlagenGroups] = useState({});
  const [vorlagenSubsectionOrderByMod, setVorlagenSubsectionOrderByMod] = useState({});
  const [isVorlagenSubsectionOrderHydrated, setIsVorlagenSubsectionOrderHydrated] = useState(false);
  const [vorlagenCategoryById, setVorlagenCategoryById] = useState({});
  const [isVorlagenCategoryHydrated, setIsVorlagenCategoryHydrated] = useState(false);
  const [vorlagenEmptyCategoriesBySubsection, setVorlagenEmptyCategoriesBySubsection] = useState({});
  const [isVorlagenEmptyCategoriesHydrated, setIsVorlagenEmptyCategoriesHydrated] = useState(false);
  const [customVorlagenGroups, setCustomVorlagenGroups] = useState([]);
  const [customBefundGroups, setCustomBefundGroups] = useState([]);
  const [customProtokollGroups, setCustomProtokollGroups] = useState([]);
  const [newVorlagenGroupName, setNewVorlagenGroupName] = useState("");
  const [newBefundGroupName, setNewBefundGroupName] = useState("");
  const [newProtokollGroupName, setNewProtokollGroupName] = useState("");
  const [modalities, setModalities] = useState([]);
  const [tableNames, setTableNames] = useState({
    vorlagen: "vorlagen",
    befundbeispiele: "befundbeispiele",
    protokolle: "protokolle",
    library: "library",
  });
  const hydrateDoneRef = useRef(false);
  const privateDataFallbackWarnedRef = useRef(false);
  const userPromptsFallbackWarnedRef = useRef(false);
  const singleSessionWarnedRef = useRef(false);
  const beurteilungGeneratePromptHydratedRef = useRef(false);
  const saveTemplateInFlightRef = useRef(false);
  const saveNewTemplateInFlightRef = useRef(false);
  const saveNewPromptInFlightRef = useRef(false);
  const sessionRef = useRef(null);
  sessionRef.current = session;

  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [isSavingNewTemplate, setIsSavingNewTemplate] = useState(false);
  const [isSavingNewProtokoll, setIsSavingNewProtokoll] = useState(false);
  const [isSavingNewPrompt, setIsSavingNewPrompt] = useState(false);

  const deferredVorlagenFilterText = useDeferredValue(vorlagenFilterText);
  const deferredBefundFilterText = useDeferredValue(filterText);
  const deferredProtokollFilterText = useDeferredValue(protokollFilterText);
  const freshStartAppliedRef = useRef(false);

  const themeColors = isDarkTheme ? DARK : LIGHT;
  const sessionEmail = (session?.user?.email || "").toLowerCase();
  const isAdmin = !!session && isAccessAdmin;
  const isDemoUser = !!session && DEMO_EMAILS.includes(sessionEmail);
  const sessionUserId = session?.user?.id || "";
  const userVorlagenStorageKey = createUserScopedStorageKey(USER_VORLAGEN_STORAGE_KEY, sessionUserId);
  const userBefundbeispieleStorageKey = createUserScopedStorageKey(USER_BEFUNDBEISPIELE_STORAGE_KEY, sessionUserId);
  const userProtokolleStorageKey = createUserScopedStorageKey(USER_PROTOKOLLE_STORAGE_KEY, sessionUserId);
  const userVorlagenGroupsStorageKey = createUserScopedStorageKey(USER_VORLAGEN_GROUPS_STORAGE_KEY, sessionUserId);
  const userBefundGroupsStorageKey = createUserScopedStorageKey(USER_BEFUNDBEISPIELE_GROUPS_STORAGE_KEY, sessionUserId);
  const userProtokollGroupsStorageKey = createUserScopedStorageKey(USER_PROTOKOLLE_GROUPS_STORAGE_KEY, sessionUserId);
  const userVorlagenGroupOrderStorageKey = createUserScopedStorageKey(USER_VORLAGEN_GROUP_ORDER_STORAGE_KEY, sessionUserId);
  const userBefundGroupOrderStorageKey = createUserScopedStorageKey(USER_BEFUNDBEISPIELE_GROUP_ORDER_STORAGE_KEY, sessionUserId);
  const userProtokollGroupOrderStorageKey = createUserScopedStorageKey(USER_PROTOKOLLE_GROUP_ORDER_STORAGE_KEY, sessionUserId);
  const userVorlagenSubsectionOrderStorageKey = createUserScopedStorageKey(USER_VORLAGEN_SUBSECTION_ORDER_STORAGE_KEY, sessionUserId);
  const userVorlagenCategoryStorageKey = createUserScopedStorageKey(USER_VORLAGEN_CATEGORY_STORAGE_KEY, sessionUserId);
  const userVorlagenEmptyCategoriesStorageKey = createUserScopedStorageKey(USER_VORLAGEN_EMPTY_CATEGORIES_STORAGE_KEY, sessionUserId);
  const userLibraryStorageKey = createUserScopedStorageKey(USER_LIBRARY_STORAGE_KEY, sessionUserId);
  // ...duplirana deklaracija uklonjena, koristi se globalna verzija...
  const userBeurteilungGeneratePromptStorageKey = createUserScopedStorageKey(BEURTEILUNG_GENERATE_PROMPT_STORAGE_KEY, sessionUserId);

  const visibleTabs = useMemo(
    () => APP_TABS.filter((tab) => tab.key !== "freigaben" && (!tab.adminOnly || isAdmin)),
    [isAdmin]
  );

  const requireAuth = () => {
    if (!supabase) { showStatus("Supabase ist nicht konfiguriert.", true); return false; }
    if (!session) {
      showStatus("Bitte zuerst anmelden.", true);
      return false;
    }
    return true;
  };

  const upsertActiveSession = async (nextSession) => {
    if (!SINGLE_SESSION_ENFORCED) return true;
    if (!supabase || !nextSession?.user?.id) return true;
    const sessionId = getSessionIdFromAccessToken(nextSession?.access_token);
    if (!sessionId) return true;

    const payload = {
      user_id: nextSession.user.id,
      session_id: sessionId,
      updated_at: new Date().toISOString(),
    };

    const { error } = await supabase
      .from(ACTIVE_SESSIONS_TABLE)
      .upsert(payload, { onConflict: "user_id" });

    if (error && !isMissingSessionTableError(error)) {
      console.error("Fehler beim Registrieren der aktiven Session:", error);
    }

    return !error || isMissingSessionTableError(error);
  };

  const enforceSingleSession = async (nextSession) => {
    if (!SINGLE_SESSION_ENFORCED) return true;
    if (!supabase || !nextSession?.user?.id) return true;
    const sessionId = getSessionIdFromAccessToken(nextSession?.access_token);
    if (!sessionId) return true;

    const { data, error } = await supabase
      .from(ACTIVE_SESSIONS_TABLE)
      .select("session_id")
      .eq("user_id", nextSession.user.id)
      .maybeSingle();

    if (error) {
      if (!isMissingSessionTableError(error)) {
        console.error("Fehler beim Prüfen der aktiven Session:", error);
      }
      return true;
    }

    if (!data?.session_id) {
      await upsertActiveSession(nextSession);
      return true;
    }

    if (String(data.session_id) === sessionId) return true;

    if (!singleSessionWarnedRef.current) {
      singleSessionWarnedRef.current = true;
      showStatus("Dieses Konto ist auf einem anderen Gerät aktiv. Sie wurden hier abgemeldet.", true);
    }
    await supabase.auth.signOut();
    return false;
  };

  const persistUserBefundbeispiele = (nextBefundbeispiele) => {
    if (!sessionUserId || isAdmin) return;
    void saveUserPrivateData(USER_DATA_KEY_BEFUNDBEISPIELE, nextBefundbeispiele);
  };

  const persistUserProtokolle = (nextProtokolle) => {
    if (!sessionUserId || isAdmin) return;
    void saveUserPrivateData(USER_DATA_KEY_PROTOKOLLE, nextProtokolle);
  };

  // Persistence of custom Vorlagen groups is handled reactively by the useEffect
  // that watches `customVorlagenGroups` state and saves to Supabase via saveUserPrivateData.
  // This callback is kept as a stable no-op so call sites in useGroupManagement don't break.
  const persistCustomVorlagenGroups = useCallback((_nextGroups) => {}, []);

  // Same as persistCustomVorlagenGroups — Supabase save is handled by the useEffect
  // watching `customBefundGroups`.
  const persistCustomBefundGroups = useCallback((_nextGroups) => {}, []);

  const persistCustomProtokollGroups = useCallback((_nextGroups) => {}, []);

  const showStatus = useCallback((msg, isErr = false) => {
    if (isErr) console.warn(msg);
  }, []);

  const maybeWarnPrivateFallback = useCallback(() => {
    if (privateDataFallbackWarnedRef.current) return;
    privateDataFallbackWarnedRef.current = true;
    setAiPromptSyncStatus("error");
    showStatus("Hinweis: Cloud-Sync für private Daten ist nicht verfügbar. Änderungen werden nicht lokal gespeichert und sind erst nach Wiederherstellung von Supabase wieder verfügbar.", true);
  }, [showStatus]);

  const maybeWarnUserPromptsFallback = useCallback(() => {
    if (userPromptsFallbackWarnedRef.current) return;
    userPromptsFallbackWarnedRef.current = true;
    showStatus("Hinweis: user_prompts derzeit nicht verfügbar. Vorlagen können aktuell nicht in der Cloud gespeichert werden.", true);
  }, [showStatus]);

  const userPrivateDataService = useMemo(() => createUserPrivateDataService({
    supabase,
    tableName: USER_PRIVATE_DATA_TABLE,
    sessionUserId,
    starterUserId: STARTER_USER_ID,
    isMissingRelationError,
    onMissingRelationError: maybeWarnPrivateFallback,
  }), [sessionUserId, maybeWarnPrivateFallback]);

  const loadUserPrivateData = useCallback((...args) => userPrivateDataService.load(...args), [userPrivateDataService]);
  const saveUserPrivateData = useCallback((...args) => userPrivateDataService.save(...args), [userPrivateDataService]);
  const saveUserPrivateDataDetailed = useCallback((...args) => userPrivateDataService.saveDetailed(...args), [userPrivateDataService]);

  const persistUserVorlagen = useCallback(async (nextVorlagen) => {
    if (!sessionUserId) return;
    return saveUserPrivateData(USER_DATA_KEY_VORLAGEN, nextVorlagen);
  }, [saveUserPrivateData, sessionUserId]);

  const {
    externalResources,
    libraryLoadInfo,
    newResourceName,
    setNewResourceName,
    newResourceUrl,
    setNewResourceUrl,
    editingResource,
    setEditingResource,
    loadExternalResources,
    createExternalResource,
    updateExternalResource,
    deleteExternalResource,
    moveExternalResource,
    reorderExternalResource,
    resetExternalResourceDrafts,
  } = useExternalResources({
    supabase,
    isAdmin,
    tableNames,
    userLibraryStorageKey,
    userDataKeyLibrary: USER_DATA_KEY_LIBRARY,
    loadUserPrivateData,
    saveUserPrivateData,
    showStatus,
    createId: createTimeBasedId,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const accessControlService = useMemo(() => createAccessControlService({
    supabase,
    accessRequestsTable: ACCESS_REQUESTS_TABLE,
    isMissingRelationError,
  }), []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const aiService = useMemo(() => createAiService({
    endpoint: AI_BEURTEILUNG_ENDPOINT,
    openaiModel: OPENAI_MODEL,
    anthropicModel: ANTHROPIC_MODEL,
    getAccessToken: () => sessionRef.current?.access_token || "",
  }), []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tableNameService = useMemo(() => createTableNameService({ supabase }), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const vorlagenService = useMemo(() => createVorlagenService({ supabase }), []);

  const userPromptsService = useMemo(() => createUserPromptsService({
    supabase,
    tableName: USER_PROMPTS_TABLE,
    sessionUserId,
    isMissingRelationError,
    onMissingRelationError: maybeWarnUserPromptsFallback,
    createId: createTimeBasedId,
  }), [sessionUserId, maybeWarnUserPromptsFallback]);

  // --- user_prompts for Vorlagen: cloud-only ---
  const userAiQuickPromptsStorageKey = createUserScopedStorageKey(USER_AI_QUICK_PROMPTS_STORAGE_KEY, sessionUserId);
  const userAiBeurteilungQuickPromptsStorageKey = createUserScopedStorageKey(USER_AI_BEURTEILUNG_QUICK_PROMPTS_STORAGE_KEY, sessionUserId);

  const loadUserPromptsFromTable = async () => {
    return userPromptsService.loadVorlagen();
  };

  const upsertUserPromptInTable = async (prompt) => {
    return userPromptsService.upsertPrompt(prompt);
  };

  const deleteUserPromptInTable = async (promptId) => {
    return userPromptsService.deletePrompt(promptId);
  };

  const persistBeurteilungGeneratePrompt = useCallback(async (nextPrompt) => {
    if (!sessionUserId || !supabase) return false;
    const payload = String(nextPrompt || "").trim() || DEFAULT_BEURTEILUNG_GENERATE_PROMPT;

    try {
      const { error } = await supabase
        .from(USER_PRIVATE_DATA_TABLE)
        .upsert(
          {
            user_id: sessionUserId,
            data_key: USER_DATA_KEY_BEURTEILUNG_GENERATE_PROMPT,
            data: payload,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "user_id,data_key" }
        );

      if (error) {
        if (isMissingRelationError(error)) {
          console.warn("user_private_data-Tabelle fehlt: Beurteilung-Generierungs-Prompt konnte nicht in Supabase gespeichert werden.");
          return false;
        }
        throw error;
      }

      return true;
    } catch (err) {
      console.error("Fehler beim Speichern des Beurteilung-Generierungs-Prompts:", err);
      return false;
    }
  }, [sessionUserId]);

  const {
    syncOwnAccessRequest,
    loadAccessRequests,
    updateAccessRequestStatus,
    resendAccessApprovedMail,
  } = useAccessManagement({
    accessControlService,
    isAdmin,
    isAccessControlAvailable,
    setIsAccessAdmin,
    setIsAccessControlAvailable,
    setAccessRecord,
    setAccessStatus,
    setAccessRequests,
    setIsAccessRequestsLoading,
    sessionUserId,
    showStatus,
  });

  const initTableNames = async () => {
    const resolved = await tableNameService.resolveInitialTableNames(tableNames);
    setTableNames(resolved);
    return resolved;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const bootstrap = async () => {
      const resolvedTables = await initTableNames();
      await Promise.all([
        loadAllBefundbeispiele(resolvedTables),
        loadAllProtokolle(resolvedTables),
        loadAllVorlagen(resolvedTables),
        loadExternalResources(resolvedTables),
      ]);
      await loadModalities();
    };
    bootstrap();
    // Initial bootstrap only; running on every render would duplicate remote fetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let isCancelled = false;

    const hydrateCloudSessionState = async () => {
      if (!session || !sessionUserId || !supabase) {
        hydrateDoneRef.current = true;
        return;
      }
      if (!isAdmin && accessStatus !== "approved") {
        hydrateDoneRef.current = true;
        return;
      }

      try {
        const [
          reportHistoryPrivate,
          aiHistoryPrivate,
          autosaveEnabledPrivate,
          aiProviderPrivate,
          darkModePrivate,
          beurteilungStyleProfilePrivate,
          onboardingSeenPrivate,
        ] = await Promise.all([
          loadUserPrivateData(USER_DATA_KEY_REPORT_HISTORY, HISTORY_STORAGE_KEY, []),
          loadUserPrivateData(USER_DATA_KEY_AI_BEFUND_HISTORY, AI_BEFUND_HISTORY_STORAGE_KEY, []),
          loadUserPrivateData(USER_DATA_KEY_AUTOSAVE_ENABLED, AUTOSAVE_ENABLED_STORAGE_KEY, true),
          loadUserPrivateData(USER_DATA_KEY_AI_PROVIDER, AI_PROVIDER_STORAGE_KEY, DEFAULT_AI_PROVIDER),
          loadUserPrivateData(USER_DATA_KEY_DARK_MODE, "darkMode", false),
          loadUserPrivateData(USER_DATA_KEY_AI_BEURTEILUNG_STYLE_PROFILE, AI_BEURTEILUNG_STYLE_PROFILE_STORAGE_KEY, DEFAULT_BEURTEILUNG_STYLE_PROFILE),
          loadUserPrivateData(USER_DATA_KEY_ONBOARDING_SEEN, `radventura.onboardingSeen.v1.${sessionUserId}`, false),
        ]);

        if (isCancelled) return;

        const sessionState = resolveCloudSessionState({
          reportHistoryPrivate,
          aiHistoryPrivate,
          autosaveEnabledPrivate,
          aiProviderPrivate,
          darkModePrivate,
          beurteilungStyleProfilePrivate,
          onboardingSeenPrivate,
          defaultAiProvider: DEFAULT_AI_PROVIDER,
          normalizeBeurteilungStyleProfile,
        });

        setReportHistory(sessionState.reportHistory);
        setAiBefundHistory(sessionState.aiBefundHistory);
        setAutosaveEnabled(sessionState.autosaveEnabled);
        setAiProvider(sessionState.aiProvider);
        setBeurteilungStyleProfile(sessionState.beurteilungStyleProfile);
        setIsDarkTheme(sessionState.isDarkTheme);

        if (!isAdmin) {
          setShowOnboarding(!sessionState.onboardingSeen);
        }
      } catch (err) {
        console.error("Fehler beim Laden der Cloud-Sitzungsdaten:", err);
      } finally {
        hydrateDoneRef.current = true;
      }
    };

    void hydrateCloudSessionState();
    return () => {
      isCancelled = true;
    };
  }, [session, sessionUserId, isAdmin, accessStatus, loadUserPrivateData]);

  useEffect(() => {
    if (freshStartAppliedRef.current) return;
    if (!supabase) return;
    if (!sessionUserId) return;
    if (!isAdmin && accessStatus !== "approved") return;

    freshStartAppliedRef.current = true;
    setActiveTab("befundung");
    setFields({ klinischeAngaben: "", fragestellung: "", modalitat: "", region: "", geschlecht: "", befund: "", beurteilung: "" });
    setLastAutosaveAt("");
    void saveUserPrivateDataDetailed(USER_DATA_KEY_EDITOR_DRAFT, null);
  }, [sessionUserId, isAdmin, accessStatus, saveUserPrivateDataDetailed]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!hydrateDoneRef.current) return;
    if (!autosaveEnabled) return;
    if (!sessionUserId || !supabase) return;
    if (!isAdmin && accessStatus !== "approved") return;
    const hasContent = Object.values(fields).some((v) => (v || "").toString().trim() !== "");
    const handle = setTimeout(() => {
      const persistDraft = async () => {
        if (!hasContent) {
          await saveUserPrivateDataDetailed(USER_DATA_KEY_EDITOR_DRAFT, null);
          setLastAutosaveAt("");
          return;
        }
        const savedAt = new Date().toISOString();
        const persisted = await saveUserPrivateDataDetailed(USER_DATA_KEY_EDITOR_DRAFT, { fields, savedAt });
        if (persisted.ok) {
          setLastAutosaveAt(savedAt);
        }
      };
      void persistDraft().catch((err) => {
        console.error("Fehler beim Autosave:", err);
      });
    }, 700);
    return () => clearTimeout(handle);
  }, [fields, autosaveEnabled, sessionUserId, isAdmin, accessStatus, saveUserPrivateDataDetailed]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!sessionUserId || !supabase) return;
    if (!isAdmin && accessStatus !== "approved") return;
    void saveUserPrivateData(USER_DATA_KEY_AUTOSAVE_ENABLED, Boolean(autosaveEnabled));
  }, [autosaveEnabled, sessionUserId, isAdmin, accessStatus, saveUserPrivateData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!sessionUserId || !supabase) return;
    if (!isAdmin && accessStatus !== "approved") return;
    void saveUserPrivateData(USER_DATA_KEY_AI_PROVIDER, aiProvider);
  }, [aiProvider, sessionUserId, isAdmin, accessStatus, saveUserPrivateData]);

  useEffect(() => {
    if (!sessionUserId || !supabase) return;
    if (!isAdmin && accessStatus !== "approved") return;
    void saveUserPrivateData(USER_DATA_KEY_AI_BEURTEILUNG_STYLE_PROFILE, beurteilungStyleProfile);
  }, [beurteilungStyleProfile, sessionUserId, isAdmin, accessStatus, saveUserPrivateData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!sessionUserId || !beurteilungGeneratePromptHydratedRef.current) return;
    if (!isAdmin && accessStatus !== "approved") return;

    const handle = setTimeout(() => {
      void persistBeurteilungGeneratePrompt(beurteilungGeneratePrompt);
    }, 400);

    return () => clearTimeout(handle);
  }, [sessionUserId, isAdmin, accessStatus, beurteilungGeneratePrompt, persistBeurteilungGeneratePrompt]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!hydrateDoneRef.current) return;
    if (!sessionUserId || !supabase) return;
    if (!isAdmin && accessStatus !== "approved") return;
    void saveUserPrivateData(USER_DATA_KEY_REPORT_HISTORY, reportHistory);
  }, [reportHistory, sessionUserId, isAdmin, accessStatus, saveUserPrivateData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!hydrateDoneRef.current) return;
    if (!sessionUserId || !supabase) return;
    if (!isAdmin && accessStatus !== "approved") return;
    void saveUserPrivateData(USER_DATA_KEY_AI_BEFUND_HISTORY, aiBefundHistory);
  }, [aiBefundHistory, sessionUserId, isAdmin, accessStatus, saveUserPrivateData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!supabase) return;
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      if (session && SINGLE_SESSION_ENFORCED) {
        singleSessionWarnedRef.current = false;
        await upsertActiveSession(session);
      }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, nextSession) => {
      setSession(nextSession);
      if (!nextSession) {
        singleSessionWarnedRef.current = false;
        return;
      }
      if (!SINGLE_SESSION_ENFORCED) return;
      singleSessionWarnedRef.current = false;
      await upsertActiveSession(nextSession);
      await enforceSingleSession(nextSession);
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!SINGLE_SESSION_ENFORCED) return undefined;
    if (!session) return undefined;

    let cancelled = false;
    const validateNow = async () => {
      if (cancelled) return;
      await enforceSingleSession(session);
    };

    const intervalId = window.setInterval(() => {
      void validateNow();
    }, 15000);

    const onFocus = () => {
      void validateNow();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void validateNow();
      }
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    void validateNow();

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.access_token, sessionUserId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!session) {
      setAccessStatus("idle");
      setAccessRecord(null);
      setAccessRequests([]);
      setIsAccessAdmin(false);
      return;
    }

    setAccessStatus("checking");
    void syncOwnAccessRequest(session).then(({ status }) => {
      if (isAdmin || status === "approved") {
        void loadAccessRequests();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, sessionUserId, isAdmin, isAccessControlAvailable]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!session) {
      setAiPromptSyncStatus(supabase ? "signed-out" : "local");
      setBeurteilungGeneratePrompt(DEFAULT_BEURTEILUNG_GENERATE_PROMPT);
      beurteilungGeneratePromptHydratedRef.current = true;
      setAiQuickPrompts(DEFAULT_AI_QUICK_PROMPTS);
      setAiBeurteilungQuickPrompts(DEFAULT_AI_BEURTEILUNG_QUICK_PROMPTS);
      return;
    }

    if (!isAdmin && accessStatus !== "approved") {
      setAiPromptSyncStatus("waiting");
      beurteilungGeneratePromptHydratedRef.current = true;
      setAiQuickPrompts(DEFAULT_AI_QUICK_PROMPTS);
      setAiBeurteilungQuickPrompts(DEFAULT_AI_BEURTEILUNG_QUICK_PROMPTS);
      return;
    }

    setAiPromptSyncStatus("checking");
    beurteilungGeneratePromptHydratedRef.current = false;
    let isCancelled = false;

    // Protect state updates when auth/access changes while async loading is still in flight.
    const loadSessionScopedData = async () => {
      const resolvedTables = await initTableNames();
      await Promise.all([
        loadAllBefundbeispiele(resolvedTables),
        loadAllProtokolle(resolvedTables),
        loadAllVorlagen(resolvedTables),
        loadExternalResources(resolvedTables),
      ]);

      if (isCancelled) return;

      const aiQuickPromptsPrivate = await loadUserPrivateData(
        USER_DATA_KEY_AI_QUICK_PROMPTS,
        userAiQuickPromptsStorageKey,
        DEFAULT_AI_QUICK_PROMPTS
      );
      if (isCancelled) return;
      if (Array.isArray(aiQuickPromptsPrivate.value)) {
        setAiQuickPrompts(normalizeAiQuickPrompts(aiQuickPromptsPrivate.value, "quick-custom"));
      } else {
        setAiQuickPrompts(DEFAULT_AI_QUICK_PROMPTS);
      }

      const aiBeurteilungQuickPromptsPrivate = await loadUserPrivateData(
        USER_DATA_KEY_AI_BEURTEILUNG_QUICK_PROMPTS,
        userAiBeurteilungQuickPromptsStorageKey,
        DEFAULT_AI_BEURTEILUNG_QUICK_PROMPTS
      );
      if (isCancelled) return;
      if (Array.isArray(aiBeurteilungQuickPromptsPrivate.value)) {
        setAiBeurteilungQuickPrompts(normalizeAiQuickPrompts(aiBeurteilungQuickPromptsPrivate.value, "beurteilung-quick-custom"));
      } else {
        setAiBeurteilungQuickPrompts(DEFAULT_AI_BEURTEILUNG_QUICK_PROMPTS);
      }

      if (aiQuickPromptsPrivate.source === "cloud" || aiBeurteilungQuickPromptsPrivate.source === "cloud") {
        setAiPromptSyncStatus("cloud");
      } else {
        setAiPromptSyncStatus("ready");
      }

      const beurteilungGeneratePromptPrivate = await loadUserPrivateData(
        USER_DATA_KEY_BEURTEILUNG_GENERATE_PROMPT,
        userBeurteilungGeneratePromptStorageKey,
        DEFAULT_BEURTEILUNG_GENERATE_PROMPT
      );

      if (isCancelled) return;

      const resolvedPrompt = String(beurteilungGeneratePromptPrivate.value || "").trim() || DEFAULT_BEURTEILUNG_GENERATE_PROMPT;
      setBeurteilungGeneratePrompt(resolvedPrompt);
      beurteilungGeneratePromptHydratedRef.current = true;
    };

    loadSessionScopedData();
    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, sessionUserId, isAdmin, accessStatus, userAiQuickPromptsStorageKey, userAiBeurteilungQuickPromptsStorageKey]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const activeTabVisible = visibleTabs.some((tab) => tab.key === activeTab) || (isAdmin && activeTab === "freigaben");
    if (!activeTabVisible) {
      setActiveTab("befundung");
    }
  }, [activeTab, isAdmin, visibleTabs]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!sessionUserId || !supabase) return;
    if (!isAdmin && accessStatus !== "approved") return;

    let isCancelled = false;

    const hydrateCustomGroups = async () => {
      const [vorlagenGroupsPrivate, befundGroupsPrivate, protokollGroupsPrivate] = await Promise.all([
        loadUserPrivateData(USER_DATA_KEY_CUSTOM_VORLAGEN_GROUPS, userVorlagenGroupsStorageKey, []),
        loadUserPrivateData(USER_DATA_KEY_CUSTOM_BEFUND_GROUPS, userBefundGroupsStorageKey, []),
        loadUserPrivateData(USER_DATA_KEY_CUSTOM_PROTOKOLL_GROUPS, userProtokollGroupsStorageKey, []),
      ]);

      if (isCancelled) return;

      const vorlagenGroups = dedupeGroupNames(Array.isArray(vorlagenGroupsPrivate.value) ? vorlagenGroupsPrivate.value : []);
      const befundGroups = dedupeGroupNames(Array.isArray(befundGroupsPrivate.value) ? befundGroupsPrivate.value : []);
      const protokollGroups = dedupeGroupNames(Array.isArray(protokollGroupsPrivate.value) ? protokollGroupsPrivate.value : []);

      setCustomVorlagenGroups(
        vorlagenGroups.filter((group) => !findExistingGroupName(DEFAULT_VORLAGEN_GROUPS, group))
      );
      setCustomBefundGroups(
        befundGroups.filter((group) => !findExistingGroupName(DEFAULT_BEFUNDBEISPIELE_GROUPS, group))
      );
      setCustomProtokollGroups(
        protokollGroups.filter((group) => !findExistingGroupName(DEFAULT_PROTOKOLLE_GROUPS, group))
      );
    };

    void hydrateCustomGroups();
    return () => {
      isCancelled = true;
    };
  }, [sessionUserId, isAdmin, accessStatus, userVorlagenGroupsStorageKey, userBefundGroupsStorageKey, userProtokollGroupsStorageKey, loadUserPrivateData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!sessionUserId || !supabase) return;
    if (!isAdmin && accessStatus !== "approved") return;
    void saveUserPrivateData(USER_DATA_KEY_CUSTOM_VORLAGEN_GROUPS, dedupeGroupNames(customVorlagenGroups));
  }, [customVorlagenGroups, sessionUserId, isAdmin, accessStatus, saveUserPrivateData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!sessionUserId || !supabase) return;
    if (!isAdmin && accessStatus !== "approved") return;
    void saveUserPrivateData(USER_DATA_KEY_CUSTOM_BEFUND_GROUPS, dedupeGroupNames(customBefundGroups));
  }, [customBefundGroups, sessionUserId, isAdmin, accessStatus, saveUserPrivateData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!sessionUserId || !supabase) return;
    if (!isAdmin && accessStatus !== "approved") return;
    void saveUserPrivateData(USER_DATA_KEY_CUSTOM_PROTOKOLL_GROUPS, dedupeGroupNames(customProtokollGroups));
  }, [customProtokollGroups, sessionUserId, isAdmin, accessStatus, saveUserPrivateData]);
  useEffect(() => {
    document.body.setAttribute("data-theme", isDarkTheme ? "dark" : "light");
    if (!sessionUserId || !supabase) return;
    if (!isAdmin && accessStatus !== "approved") return;
    void saveUserPrivateData(USER_DATA_KEY_DARK_MODE, Boolean(isDarkTheme));
  }, [isDarkTheme, sessionUserId, isAdmin, accessStatus, saveUserPrivateData]);

  useEffect(() => {
    if (!session || !sessionUserId || !supabase) return;
    if (!isAdmin && accessStatus !== "approved") return;

    let cancelled = false;

    const runPrivateDataHealthCheck = async () => {
      const probeKey = "radventura.policy.healthcheck.v1";
      const probePayload = { checked_at: new Date().toISOString() };

      const { error: upsertError } = await supabase
        .from(USER_PRIVATE_DATA_TABLE)
        .upsert(
          {
            user_id: sessionUserId,
            data_key: probeKey,
            data: probePayload,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "user_id,data_key" }
        );

      if (cancelled) return;

      if (upsertError) {
        setAiPromptSyncStatus("error");
        console.warn(`Cloud-Sync-Check fehlgeschlagen (write): ${upsertError.message || upsertError.code || "Unbekannter Fehler"}`);
        return;
      }

      const { error: selectError } = await supabase
        .from(USER_PRIVATE_DATA_TABLE)
        .select("updated_at")
        .eq("user_id", sessionUserId)
        .eq("data_key", probeKey)
        .maybeSingle();

      if (cancelled) return;

      if (selectError) {
        setAiPromptSyncStatus("error");
        console.warn(`Cloud-Sync-Check fehlgeschlagen (read): ${selectError.message || selectError.code || "Unbekannter Fehler"}`);
      }
    };

    void runPrivateDataHealthCheck();

    return () => {
      cancelled = true;
    };
  }, [session, sessionUserId, isAdmin, accessStatus]);

  const {
    fieldHistory,
    fieldFuture,
    updateField,
    undoField,
    redoField,
    applyBefundWithHistory,
    applyBeurteilungWithHistory,
    resetFieldHistory,
  } = useEditorFieldHistory({
    fields,
    setFields,
    setAiBefundPrompt,
    setAiBeurteilungPrompt,
    setAiBefundHighlightRange,
    showStatus,
  });



  const resetApp = () => {
    setFields({ klinischeAngaben: "", fragestellung: "", modalitat: "", region: "", geschlecht: "", befund: "", beurteilung: "" });
    resetFieldHistory();
    setEditingId(null);
    setTemplateDraftName("");
    setTemplateDraftSection("");
    setActiveTab("befundung");
    setIsModalityDropdownOpen(false);
    setIsRegionDropdownOpen(false);
    setIsGenderDropdownOpen(false);
    setNewSectionName("");
    setNewSectionMod("CT");
    setExpandedSections({});
    setFilterText("");
    setDeleteConfirm({ show: false, id: null, modGroup: null, section: null });
    setSectionDeleteConfirm({ show: false, modGroup: null, section: null });
    setNewTplFormOpen(false);
    setNewTplMod("CT");
    setNewTplSection("");
    setNewTplCategory("");
    setNewTplName("");
    setNewTplBefund("");
    setNewTplBeurteilung("");
    setProtokollExpandedSections({});
    setProtokollFilterText("");
    setNewProtokollSectionName("");
    setNewProtokollSectionMod("CT");
    setNewProtokollFormOpen(false);
    setNewProtokollMod("CT");
    setNewProtokollSection("");
    setNewProtokollCategory("");
    setNewProtokollName("");
    setNewProtokollBefund("");
    setNewProtokollBeurteilung("");
    setProtokollDeleteConfirm({ show: false, id: null, modGroup: null, section: null });
    setProtokollSectionDeleteConfirm({ show: false, modGroup: null, section: null });
    setIsGenerating(false);
    setIsAiBefundLoading(false);
    setVorlagenFilterText("");
    setExpandedVorlagenGroups({});
    setNewPromptMod("CT");
    setNewPromptMrtTarget("MRT");
    setNewPromptSubsection("");
    setNewPromptCategory("");
    setNewPromptName("");
    setNewPromptBefund("");
    setNewPromptBeurteilung("");
    resetExternalResourceDrafts();
    setAiBefundPrompt("");
    setAiBeurteilungPrompt("");
    setAiBefundResponse("");
    setAiBefundHistory([]);
    setAiBefundLastAction(null);
    setAiBefundHighlightRange({ start: null, end: null, nonce: 0 });
    setAiBefundSelection({ start: null, end: null, text: "" });
    setLastAutosaveAt("");
  };

  const updateAiBefundSelection = useCallback((selection) => {
    setAiBefundSelection(selection);
  }, []);

  const requestAiText = async ({ systemPrompt, context, maxTokens = 1200 }) => aiService.requestAiText({
    provider: aiProvider,
    systemPrompt,
    context,
    maxTokens,
  });

  const showApiError = (err) => {
    showStatus(`API-Fehler: ${err?.message || AI_ERROR_UNKNOWN}`, true);
  };

  const buildCurrentRadiologyContext = () => buildRadiologyContext(fields);

  const compressBeurteilungIfNeeded = async (candidateText) => {
    const sourceText = String(candidateText || "").trim();
    const styleProfile = normalizeBeurteilungStyleProfile(beurteilungStyleProfile);
    if (!shouldCompressBeurteilung(sourceText, styleProfile)) {
      return sourceText;
    }

    const styleRules = getBeurteilungStyleRuleSet(styleProfile);

    const compressed = await requestAiText({
      systemPrompt: buildBeurteilungCompressorSystemPrompt(styleProfile),
      context: [
        buildCurrentRadiologyContext(),
        "\nOriginal-Beurteilung:",
        sourceText,
        "\nAnweisung: Verdichte die Beurteilung streng nach den Regeln.",
      ].join("\n"),
      maxTokens: 360,
    });

    const normalizedCompressed = String(compressed || "").trim() || sourceText;
    const maxChars = styleRules.maxChars;
    if (normalizedCompressed.length <= maxChars) return normalizedCompressed;

    // Cut at the last sentence boundary before maxChars to avoid mid-sentence truncation.
    const truncated = normalizedCompressed.slice(0, maxChars);
    const lastSentenceEnd = Math.max(
      truncated.lastIndexOf("."),
      truncated.lastIndexOf("!"),
      truncated.lastIndexOf("?")
    );
    return lastSentenceEnd > 0
      ? normalizedCompressed.slice(0, lastSentenceEnd + 1).trim()
      : truncated.trim();
  };

  const buildAiAssistantContext = ({ mode, promptText }) => buildAiAssistantContextValue({
    fields,
    mode,
    promptText,
    selectionText: aiBefundSelection.text,
    selectionOnlyInstruction: AI_SELECTION_ONLY_INSTRUCTION,
    fullBefundInstruction: AI_FULL_BEFUND_INSTRUCTION,
    userInstructionLabel: AI_USER_INSTRUCTION_LABEL,
  });

  const createAiBefundHistoryEntry = ({ prompt, response, mode }) => ({
    id: createTimeBasedId(),
    prompt,
    response,
    mode,
    selectionText: mode === "selection" ? aiBefundSelection.text.trim() : "",
    createdAt: new Date().toISOString(),
  });

  const updateKlinischeAngaben = (val) => setFields(f => ({
    ...f,
    klinischeAngaben: val,
    befund: syncLabeledLine(f.befund, "Klinische Angaben", f.klinischeAngaben, val, {
      Fragestellung: f.fragestellung,
    }),
  }));
  const updateFragestellung = (val) => setFields(f => ({
    ...f,
    fragestellung: val,
    befund: syncLabeledLine(f.befund, "Fragestellung", f.fragestellung, val, {
      "Klinische Angaben": f.klinischeAngaben,
    }),
  }));


  const { modalityOptions, modalityTree, getModalitaetenBefundLine } = useModalityDisplay({
    modalities,
    vorlagen,
    customVorlagenGroups,
  });

  const getCustomPrompt = useCallback((name, mod) => findCustomPrompt(vorlagen, name, mod), [vorlagen]);

  const loadAllBefundbeispiele = async (names = tableNames) => {
    if (!isAdmin) {
      const privateBefundbeispiele = await loadUserPrivateData(
        USER_DATA_KEY_BEFUNDBEISPIELE,
        userBefundbeispieleStorageKey,
        EMPTY_BEFUNDBEISPIELE
      );
      const nextValue = privateBefundbeispiele.value && typeof privateBefundbeispiele.value === "object"
        ? privateBefundbeispiele.value
        : EMPTY_BEFUNDBEISPIELE;
      setBefundbeispiele(nextValue);
      return;
    }
    if (!supabase) return;
    try {
      const { data, error } = await supabase.from(names.befundbeispiele).select("*").order("created_at", { ascending: false });
      if (error) throw error;
      setBefundbeispiele(mapBefundbeispieleRows(data));
    } catch (err) { console.error("Fehler beim Laden:", err); }
  };

  const loadAllProtokolle = async (names = tableNames) => {
    if (!isAdmin) {
      const privateProtokolle = await loadUserPrivateData(
        USER_DATA_KEY_PROTOKOLLE,
        userProtokolleStorageKey,
        createEmptyProtokolle()
      );
      const nextValue = privateProtokolle.value && typeof privateProtokolle.value === "object"
        ? privateProtokolle.value
        : createEmptyProtokolle();
      setProtokolle(nextValue);
      return;
    }
    if (!supabase) return;
    try {
      const { data, error } = await supabase.from(names.protokolle).select("*").order("created_at", { ascending: false });
      if (error) throw error;
      setProtokolle(mapBefundbeispieleRows(data, createEmptyProtokolle));
    } catch (err) { console.error("Fehler beim Laden:", err); }
  };

  const loadBefundbeispiel = useCallback((tpl, mode = "all") => {
    const takeBefund = mode === "all" || mode === "befund";
    const takeBeurteilung = mode === "all" || mode === "beurteilung";

    const nextBefund = takeBefund && tpl.befund
      ? appendRichText(fields.befund, tpl.befund)
      : fields.befund;
    const nextBeurteilung = takeBeurteilung && tpl.beurteilung
      ? appendRichText(fields.beurteilung, tpl.beurteilung)
      : fields.beurteilung;

    setFields((prev) => {
      const next = {
        ...prev,
        befund: nextBefund,
        beurteilung: nextBeurteilung,
      };

      // Metadata is applied only when Befund text is übernommen.
      if (takeBefund) {
        next.klinischeAngaben = prev.klinischeAngaben || tpl.klinischeAngaben || "";
        next.fragestellung = prev.fragestellung || tpl.fragestellung || "";
        next.modalitat = prev.modalitat || tpl.modalitat || "";
        next.region = prev.region || tpl.region || "";
        next.geschlecht = prev.geschlecht || tpl.geschlecht || "";
      }

      return next;
    });

    setActiveTab("befundung");
    if (mode === "befund") showStatus(`Befund aus "${tpl.name}" übernommen ✓`);
    else if (mode === "beurteilung") showStatus(`Beurteilung aus "${tpl.name}" übernommen ✓`);
    else showStatus(`Befundbeispiel "${tpl.name}" übernommen ✓`);
  }, [fields.befund, fields.beurteilung, showStatus]);

  const toggleSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleProtokollSection = (key) => setProtokollExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  const loadAllVorlagen = async (names = tableNames) => {
    if (!supabase) return;
    try {
      if (!isAdmin) {
        const userVorlagen = await loadUserPromptsFromTable();
        if (userVorlagen.supported) {
          let effectiveVorlagen = userVorlagen.vorlagen;

          // First-run bootstrap: seed from starter account snapshot, fallback to shared baseline.
          if (effectiveVorlagen.length === 0) {
            let seededPrompts = [];

            if (STARTER_USER_ID && STARTER_USER_ID !== sessionUserId) {
              const { data: starterPromptRows, error: starterPromptError } = await supabase
                .from(USER_PROMPTS_TABLE)
                .select("prompt_id,name,mod,befund,beurteilung,created_at")
                .eq("user_id", STARTER_USER_ID)
                .order("created_at", { ascending: true });

              if (!starterPromptError && Array.isArray(starterPromptRows) && starterPromptRows.length > 0) {
                seededPrompts = starterPromptRows.map((row) => mapBaselineVorlageRow(row, createTimeBasedId));
              } else {
                const { data: starterPrivateVorlagen, error: starterPrivateError } = await supabase
                  .from(USER_PRIVATE_DATA_TABLE)
                  .select("data")
                  .eq("user_id", STARTER_USER_ID)
                  .eq("data_key", USER_DATA_KEY_VORLAGEN)
                  .maybeSingle();

                if (!starterPrivateError && Array.isArray(starterPrivateVorlagen?.data)) {
                  seededPrompts = starterPrivateVorlagen.data.map((row) => mapBaselineVorlageRow(row, createTimeBasedId));
                }
              }
            }

            if (seededPrompts.length > 0) {
              await Promise.all(seededPrompts.map((entry) => upsertUserPromptInTable(entry)));
              effectiveVorlagen = seededPrompts;
            }
          }

          if (effectiveVorlagen.length === 0) {
            const { data: baselineRows, error: baselineError } = await vorlagenService.loadBaselineRows(names);

            if (!baselineError && Array.isArray(baselineRows) && baselineRows.length > 0) {
              const baselinePrompts = baselineRows.map((row) => mapBaselineVorlageRow(row, createTimeBasedId));

              await Promise.all(baselinePrompts.map((entry) => upsertUserPromptInTable(entry)));
              effectiveVorlagen = baselinePrompts;
            }
          }

          const privateVorlagenOverlay = await loadUserPrivateData(
            USER_DATA_KEY_VORLAGEN,
            userVorlagenStorageKey,
            null
          );
          if (privateVorlagenOverlay.found && Array.isArray(privateVorlagenOverlay.value) && privateVorlagenOverlay.value.length > 0) {
            effectiveVorlagen = privateVorlagenOverlay.value;
          }

          const { merged, missingDefaults } = ensureModalitaetenVorlagen(effectiveVorlagen, DEFAULT_MODALITAETEN_VORLAGEN);
          setVorlagen(merged);

          await persistUserVorlagen(merged);

          if (missingDefaults.length > 0) {
            await Promise.all(missingDefaults.map((entry) => upsertUserPromptInTable(entry)));
          }
          return;
        }

        const privateVorlagen = await loadUserPrivateData(
          USER_DATA_KEY_VORLAGEN,
          userVorlagenStorageKey,
          null
        );
        if (privateVorlagen.found && Array.isArray(privateVorlagen.value)) {
          const { merged, missingDefaults } = ensureModalitaetenVorlagen(privateVorlagen.value, DEFAULT_MODALITAETEN_VORLAGEN);
          setVorlagen(merged);
          if (missingDefaults.length > 0) {
            await persistUserVorlagen(merged);
          }
          return;
        }
      }

      const { data, error } = await supabase.from(names.vorlagen).select("*").order("created_at", { ascending: true });
      if (error) throw error;
      const privateVorlagenOverlay = await loadUserPrivateData(
        USER_DATA_KEY_VORLAGEN,
        userVorlagenStorageKey,
        null
      );
      const categoryOverlay = privateVorlagenOverlay.found && Array.isArray(privateVorlagenOverlay.value)
        ? privateVorlagenOverlay.value
        : [];
      const categoryMergedRows = mergeVorlagenCategoryOverlay(data || [], categoryOverlay);
      const { merged, missingDefaults } = ensureModalitaetenVorlagen(categoryMergedRows, DEFAULT_MODALITAETEN_VORLAGEN);
      setVorlagen(merged);

      if (isAdmin && missingDefaults.length > 0) {
        const rowsToInsert = missingDefaults.map((entry) => ({
          id: entry.id,
          name: entry.name,
          mod: entry.mod,
          befund: entry.befund,
          beurteilung: entry.beurteilung,
          created_at: entry.created_at,
        }));
        const { error: upsertError } = await supabase.from(names.vorlagen).upsert(rowsToInsert, { onConflict: "id" });
        if (upsertError) {
          console.error("Fehler beim Initialisieren der Modalitäten-Vorlagen:", upsertError);
        }
      }
    } catch (err) { console.error("Fehler beim Laden der Vorlagen:", err); }
  };

  const {
    deletePromptById,
    saveVorlageInline,
    renameVorlagenSubsection,
    deleteVorlagenSubsection,
  } = useVorlagenOperations({
    isAdmin,
    vorlagen,
    setVorlagen,
    persistUserVorlagen,
    upsertUserPromptInTable,
    deleteUserPromptInTable,
    vorlagenService,
    tableNames,
    loadAllVorlagen,
    showStatus,
    formatSupabaseError,
  });

  const loadModalities = async () => {
    if (!supabase) return;
    try {
      const { data, error } = await supabase
        .from("modalities")
        .select("*")
        .order("sort_order", { ascending: true });
      if (error) throw error;
      if (Array.isArray(data) && data.length > 0) setModalities(data);
    } catch (err) {
      console.error("Fehler beim Laden der Modalities:", err);
    }
  };

  const doSaveNewPrompt = async () => {
    const validationError = validateNewVorlageInput({
      name: newPromptName,
      befund: newPromptBefund,
    });
    if (validationError) { showStatus(validationError, true); return; }
    if (saveNewPromptInFlightRef.current) return;

    saveNewPromptInFlightRef.current = true;
    setIsSavingNewPrompt(true);

    try {
      const targetMod = resolvePromptTargetMod(newPromptMod, newPromptMrtTarget);
      if (
        targetMod &&
        !findExistingGroupName(DEFAULT_VORLAGEN_GROUPS, targetMod) &&
        !findExistingGroupName(customVorlagenGroups, targetMod)
      ) {
        const nextCustomGroups = sortGroupNames([...customVorlagenGroups, targetMod], DEFAULT_VORLAGEN_GROUPS)
          .filter((group) => !findExistingGroupName(DEFAULT_VORLAGEN_GROUPS, group));
        setCustomVorlagenGroups(nextCustomGroups);
        persistCustomVorlagenGroups(nextCustomGroups);
        updateVorlagenGroupOrder((prev) => [
          ...(prev.length ? prev : availableVorlagenGroups),
          targetMod,
        ]);
      }

      if (!isAdmin) {
        const newPromptEntry = createNewVorlageEntry({
          newPromptMod,
          newPromptMrtTarget,
          newPromptSubsection,
          newPromptCategory,
          newPromptName,
          newPromptBefund,
          newPromptBeurteilung,
          createId: () => `${createTimeBasedId()}-${Math.random()}`,
          now: () => Date.now(),
        });
        const nextVorlagen = [...vorlagen, newPromptEntry];

        const userPromptSave = await upsertUserPromptInTable(newPromptEntry);
        if (!userPromptSave.ok && userPromptSave.supported) {
          showStatus(`Fehler beim Speichern: ${formatSupabaseError(userPromptSave.error)}`, true);
          return;
        }

        setVorlagen(nextVorlagen);
        if (newPromptCategory.trim()) {
          updateVorlageCategory(newPromptEntry, newPromptCategory);
        }
        await persistUserVorlagen(nextVorlagen);
        showStatus("Vorlage gespeichert ✓");
        setNewPromptFormOpen(false);
        setNewPromptMod("CT");
        setNewPromptMrtTarget("MRT");
        setNewPromptSubsection("");
        setNewPromptCategory("");
        setNewPromptName("");
        setNewPromptBefund("");
        setNewPromptBeurteilung("");
        return;
      }

      const id = Date.now().toString();
      const normalizedPrompt = normalizeVorlageForSave({
        id,
        name: newPromptName,
        subsection: newPromptSubsection,
        category: newPromptCategory,
        mod: targetMod,
        befund: newPromptBefund.trim(),
        beurteilung: newPromptBeurteilung.trim() || null,
      });
      const { error } = await vorlagenService.insertPrompt(tableNames, {
        id,
        name: normalizedPrompt.name,
        mod: targetMod,
        befund: newPromptBefund.trim(),
        beurteilung: newPromptBeurteilung.trim() || null,
        created_at: Date.now(),
      });
      if (error) throw error;
      showStatus("Vorlage gespeichert ✓");
      if (newPromptCategory.trim()) {
        updateVorlageCategory(normalizedPrompt, newPromptCategory);
      }
      await loadAllVorlagen();
      setNewPromptFormOpen(false);
      setNewPromptMod("CT");
      setNewPromptMrtTarget("MRT");
      setNewPromptSubsection("");
      setNewPromptCategory("");
      setNewPromptName(""); setNewPromptBefund(""); setNewPromptBeurteilung("");
    } catch (err) {
      showStatus(`Fehler beim Speichern: ${formatSupabaseError(err)}`, true);
    } finally {
      saveNewPromptInFlightRef.current = false;
      setIsSavingNewPrompt(false);
    }
  };

  const doDeletePrompt = async () => {
    const wasDeleted = await deletePromptById(promptDeleteConfirm.id);
    if (wasDeleted) {
      setPromptDeleteConfirm({ show: false, id: null });
    }
  };

  const toggleVorlagenGroup = useCallback((key) => setExpandedVorlagenGroups((prev) => ({ ...prev, [key]: !prev[key] })), []);

  const filterVorlagen = useCallback((list) => {
    if (!deferredVorlagenFilterText) return list;
    const q = deferredVorlagenFilterText.toLowerCase();
    return list.filter(p =>
      (p.name || "").toLowerCase().includes(q) ||
      getVorlageCategoryValue(vorlagenCategoryById, p).toLowerCase().includes(q) ||
      richTextToPlainText(p.befund).toLowerCase().includes(q) ||
      richTextToPlainText(p.beurteilung).toLowerCase().includes(q)
    );
  }, [deferredVorlagenFilterText, vorlagenCategoryById]);

  const loadVorlageToEditor = useCallback((prompt, mode = "all") => {
    const takeBefund = mode === "all" || mode === "befund";
    const takeBeurteilung = mode === "all" || mode === "beurteilung";
    const promptName = getVorlageDisplayName(prompt);
    const promptRegion = getVorlageActualRegion(prompt);
    const promptRegionValue = String(prompt?.name || "").trim();

    const nextBefund = takeBefund && prompt.befund
      ? appendRichText(fields.befund, prompt.befund)
      : fields.befund;
    const nextBeurteilung = takeBeurteilung && prompt.beurteilung
      ? appendRichText(fields.beurteilung, prompt.beurteilung)
      : fields.beurteilung;

    setFields((prev) => {
      const next = {
        ...prev,
        befund: nextBefund,
        beurteilung: nextBeurteilung,
      };

      if (takeBefund) {
        if (prompt.mod === "Volumetrie") {
          if (promptName === "Mann" || promptName === "Frau") next.geschlecht = promptName;
        } else if (prompt.mod === "Modalitäten") {
          next.modalitat = promptName || prev.modalitat;
          if (prev.modalitat !== promptName) {
            next.region = "";
          }
        } else {
          next.modalitat = prev.modalitat || prompt.mod;
          next.region = promptRegionValue || promptRegion || prev.region;
        }
      }

      return next;
    });

    setActiveTab("befundung");
    if (mode === "befund") showStatus(`Befund aus Vorlage "${promptName}" übernommen ✓`);
    else if (mode === "beurteilung") showStatus(`Beurteilung aus Vorlage "${promptName}" übernommen ✓`);
    else showStatus(`Vorlage "${promptName}" übernommen ✓`);
  }, [fields.befund, fields.beurteilung, showStatus]);

  const {
    generateBeurteilung,
    runAiBefundAssistant,
    runAiBeurteilungAssistant,
    restoreAiBefundHistoryEntry,
    deleteAiBefundHistoryEntry,
    clearAiBefundHistory,
    saveAiQuickPrompt,
    deleteAiQuickPrompt,
    saveAiBeurteilungQuickPrompt,
    deleteAiBeurteilungQuickPrompt,
    saveBeurteilungGeneratePromptHandler,
    applyAiBefundResponse,
  } = useAiWorkflowOperations({
    fields,
    setFields,
    beurteilungGeneratePrompt,
    defaultBeurteilungGeneratePrompt: DEFAULT_BEURTEILUNG_GENERATE_PROMPT,
    systemPrompt: SYSTEM_PROMPT,
    buildCurrentRadiologyContext,
    buildBeurteilungStyleInstruction,
    normalizeBeurteilungStyleProfile,
    beurteilungStyleProfile,
    aiUserInstructionLabel: AI_USER_INSTRUCTION_LABEL,
    requestAiText,
    compressBeurteilungIfNeeded,
    setIsGenerating,
    showStatus,
    showApiError,
    aiBefundPrompt,
    aiBeurteilungPrompt,
    aiBefundResponse,
    aiBefundSelection,
    setIsAiBefundLoading,
    setIsAiBeurteilungLoading,
    setAiBefundResponse,
    setAiBefundPrompt,
    buildAiAssistantContext,
    aiBefundAssistantSystemPrompt: AI_BEFUND_ASSISTANT_SYSTEM_PROMPT,
    extractBefundOnly,
    aiMsgEnterBefundFirst: AI_MSG_ENTER_BEFUND_FIRST,
    aiMsgEnterPromptFirst: AI_MSG_ENTER_PROMPT_FIRST,
    aiMsgSelectBefundFirst: AI_MSG_SELECT_BEFUND_FIRST,
    aiMsgNoResponse: AI_MSG_NO_RESPONSE,
    aiMsgEnterBeurteilungFirst: AI_MSG_ENTER_BEURTEILUNG_FIRST,
    applyBefundWithHistory,
    applyBeurteilungWithHistory,
    deriveChangedRange,
    createAiBefundHistoryEntry,
    setAiBefundHistory,
    aiBefundHistoryLimit: AI_BEFUND_HISTORY_LIMIT,
    setAiBefundLastAction,
    aiBefundHistory,
    aiMsgHistoryEntryLoaded: AI_MSG_HISTORY_ENTRY_LOADED,
    aiMsgHistoryEntryRemoved: AI_MSG_HISTORY_ENTRY_REMOVED,
    aiMsgHistoryCleared: AI_MSG_HISTORY_CLEARED,
    createTimeBasedId,
    aiQuickPrompts,
    setAiQuickPrompts,
    aiBeurteilungQuickPrompts,
    setAiBeurteilungQuickPrompts,
    aiQuickPromptsLimit: AI_QUICK_PROMPTS_LIMIT,
    saveUserPrivateDataDetailed,
    setAiPromptSyncStatus,
    formatSupabaseError,
    userDataKeyAiQuickPrompts: USER_DATA_KEY_AI_QUICK_PROMPTS,
    userDataKeyAiBeurteilungQuickPrompts: USER_DATA_KEY_AI_BEURTEILUNG_QUICK_PROMPTS,
    aiMsgPromptSaved: AI_MSG_PROMPT_SAVED,
    aiMsgPromptDeleted: AI_MSG_PROMPT_DELETED,
    aiMsgBeurteilungPromptSaved: AI_MSG_BEURTEILUNG_PROMPT_SAVED,
    aiMsgBeurteilungPromptDeleted: AI_MSG_BEURTEILUNG_PROMPT_DELETED,
    requireAuth,
    persistBeurteilungGeneratePrompt,
  });

  const copyText = (text) => {
    copyRichTextToClipboard(text)
      .then(() => showStatus("Kopiert ✓"))
      .catch(() => showStatus("Kopieren fehlgeschlagen. Bitte Browser-Berechtigung prüfen.", true));
  };

  const vorlagenForMod = useCallback((mod) => {
    if (!isMrtVariant(mod)) return vorlagen.filter((p) => p.mod === mod);

    const exact = vorlagen.filter((p) => p.mod === mod);
    const sharedMrt = vorlagen.filter((p) => p.mod === "MRT");

    // If same name exists in exact and shared, prefer exact (e.g. Herz mapping variants).
    const byName = new Map();
    exact.forEach((p) => byName.set(p.name, p));
    sharedMrt.forEach((p) => {
      if (!byName.has(p.name)) byName.set(p.name, p);
    });
    return Array.from(byName.values());
  }, [vorlagen]);

  const regionOptionsForMod = useCallback((mod) => {
    const relevantPrompts = mod
      ? vorlagenForMod(mod)
      : vorlagen.filter((p) => p.mod !== "Volumetrie" && p.mod !== "Modalitäten");

    const seenDisplayNames = new Set();
    return relevantPrompts.reduce((acc, prompt) => {
      const value = String(prompt?.name || "").trim();
      const displayName = getVorlageActualRegion(prompt);
      if (!value || !displayName || seenDisplayNames.has(displayName)) return acc;

      seenDisplayNames.add(displayName);

      acc.push({
        value,
        displayName,
        label: displayName,
      });
      return acc;
    }, []);
  }, [vorlagen, vorlagenForMod]);

  const getRegionDisplayLabel = useCallback((regionValue, mod) => {
    const normalizedRegionValue = String(regionValue || "").trim();
    if (!normalizedRegionValue) return "";

    const exactOption = regionOptionsForMod(mod).find((option) => option.value === normalizedRegionValue);
    if (exactOption) return exactOption.label;

    const prompt = getCustomPrompt(normalizedRegionValue, mod);
    if (!prompt) return normalizedRegionValue;

    const displayName = getVorlageActualRegion(prompt);
    return displayName;
  }, [getCustomPrompt, regionOptionsForMod]);

  const {
    saveCurrentToHistory,
    restoreFromHistory,
    deleteHistoryEntry,
    clearHistory,
  } = useReportHistory({
    reportHistory,
    setReportHistory,
    fields,
    setFields,
    showStatus,
    getRegionDisplayLabel,
  });

  const {
    selectModality,
    selectRegion,
    selectGeschlecht,
  } = useFieldSelectors({
    fields,
    setFields,
    vorlagen,
    getCustomPrompt,
    getModalitaetenBefundLine,
    getRegionDisplayLabel,
    setIsModalityDropdownOpen,
    setIsRegionDropdownOpen,
    setIsGenderDropdownOpen,
    showStatus,
  });

  const regionTreeForMod = useCallback((mod) => {
    const relevantPrompts = mod
      ? vorlagenForMod(mod)
      : vorlagen.filter((p) => p.mod !== "Volumetrie" && p.mod !== "Modalitäten");

    const sectionsMap = new Map();
    const direct = [];
    const seenValues = new Set();

    relevantPrompts.forEach((prompt) => {
      const value = String(prompt?.name || "").trim();
      const displayName = getVorlageActualRegion(prompt);
      if (!value || !displayName || seenValues.has(value)) return;
      seenValues.add(value);

      const subsection = getVorlageSubsection(prompt);
      const category = getVorlageCategoryValue(vorlagenCategoryById, prompt);
      const item = { value, label: displayName, displayName, category };
      if (subsection) {
        if (!sectionsMap.has(subsection)) sectionsMap.set(subsection, []);
        sectionsMap.get(subsection).push(item);
      } else {
        direct.push(item);
      }
    });

    const sections = Array.from(sectionsMap.entries()).map(([name, items]) => {
      const categoriesMap = new Map();
      items.forEach((item) => {
        const category = String(item.category || "").trim() || "Allgemein";
        if (!categoriesMap.has(category)) categoriesMap.set(category, []);
        categoriesMap.get(category).push(item);
      });

      const categories = Array.from(categoriesMap.entries())
        .map(([categoryName, categoryItems]) => ({ name: categoryName, items: categoryItems }))
        .sort((a, b) => {
          if (a.name === "Allgemein") return -1;
          if (b.name === "Allgemein") return 1;
          return a.name.localeCompare(b.name, "de", { numeric: true, sensitivity: "base" });
        });

      const hasNestedCategories = categories.some((category) => category.name !== "Allgemein");
      return { name, items, categories, hasNestedCategories };
    });
    return { sections, direct };
  }, [vorlagen, vorlagenCategoryById, vorlagenForMod]);

  const selectedRegionLabel = useMemo(
    () => getRegionDisplayLabel(fields.region, fields.modalitat),
    [fields.region, fields.modalitat, getRegionDisplayLabel]
  );

  const vorlagenMods = useMemo(() => vorlagen.map((prompt) => prompt?.mod), [vorlagen]);
  const befundGroupNames = useMemo(() => Object.keys(befundbeispiele || {}), [befundbeispiele]);
  const protokollGroupNames = useMemo(() => Object.keys(protokolle || {}), [protokolle]);
  const [isGroupOrderHydrated, setIsGroupOrderHydrated] = useState(false);

  const {
    availableVorlagenGroups,
    availableBefundGroups,
    availableProtokollGroups,
    updateVorlagenGroupOrder,
    updateBefundGroupOrder,
    updateProtokollGroupOrder,
  } = useGroupOrder({
    dedupeGroupNames,
    readJsonStorage,
    writeJsonStorage,
    userVorlagenGroupOrderStorageKey,
    userBefundGroupOrderStorageKey,
    userProtokollGroupOrderStorageKey,
    defaultVorlagenGroups: DEFAULT_VORLAGEN_GROUPS,
    defaultBefundGroups: DEFAULT_BEFUNDBEISPIELE_GROUPS,
    defaultProtokollGroups: DEFAULT_PROTOKOLLE_GROUPS,
    customVorlagenGroups,
    customBefundGroups,
    customProtokollGroups,
    vorlagenMods,
    befundGroupNames,
    protokollGroupNames,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setIsGroupOrderHydrated(false);
    if (!sessionUserId || !supabase) {
      setIsGroupOrderHydrated(true);
      return;
    }
    if (!isAdmin && accessStatus !== "approved") {
      setIsGroupOrderHydrated(true);
      return;
    }

    let isCancelled = false;

    const hydrateGroupOrder = async () => {
      try {
        const [vorlagenOrderPrivate, befundOrderPrivate, protokollOrderPrivate] = await Promise.all([
          loadUserPrivateData(USER_DATA_KEY_VORLAGEN_GROUP_ORDER, userVorlagenGroupOrderStorageKey, []),
          loadUserPrivateData(USER_DATA_KEY_BEFUND_GROUP_ORDER, userBefundGroupOrderStorageKey, []),
          loadUserPrivateData(USER_DATA_KEY_PROTOKOLL_GROUP_ORDER, userProtokollGroupOrderStorageKey, []),
        ]);

        if (isCancelled) return;

        if (Array.isArray(vorlagenOrderPrivate.value)) {
          updateVorlagenGroupOrder(dedupeGroupNames(vorlagenOrderPrivate.value));
        }
        if (Array.isArray(befundOrderPrivate.value)) {
          updateBefundGroupOrder(dedupeGroupNames(befundOrderPrivate.value));
        }
        if (Array.isArray(protokollOrderPrivate.value)) {
          updateProtokollGroupOrder(dedupeGroupNames(protokollOrderPrivate.value));
        }
      } finally {
        if (!isCancelled) setIsGroupOrderHydrated(true);
      }
    };

    void hydrateGroupOrder();
    return () => {
      isCancelled = true;
    };
  }, [
    sessionUserId,
    isAdmin,
    accessStatus,
    userVorlagenGroupOrderStorageKey,
    userBefundGroupOrderStorageKey,
    userProtokollGroupOrderStorageKey,
    updateVorlagenGroupOrder,
    updateBefundGroupOrder,
    updateProtokollGroupOrder,
    loadUserPrivateData,
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!sessionUserId || !supabase) return;
    if (!isAdmin && accessStatus !== "approved") return;
    if (!isGroupOrderHydrated) return;
    void saveUserPrivateData(USER_DATA_KEY_VORLAGEN_GROUP_ORDER, dedupeGroupNames(availableVorlagenGroups));
  }, [availableVorlagenGroups, sessionUserId, isAdmin, accessStatus, isGroupOrderHydrated, saveUserPrivateData]);

  useEffect(() => {
    if (!sessionUserId || !supabase) return;
    if (!isAdmin && accessStatus !== "approved") return;
    if (!isGroupOrderHydrated) return;
    void saveUserPrivateData(USER_DATA_KEY_BEFUND_GROUP_ORDER, dedupeGroupNames(availableBefundGroups));
  }, [availableBefundGroups, sessionUserId, isAdmin, accessStatus, isGroupOrderHydrated, saveUserPrivateData]);

  useEffect(() => {
    if (!sessionUserId || !supabase) return;
    if (!isAdmin && accessStatus !== "approved") return;
    if (!isGroupOrderHydrated) return;
    void saveUserPrivateData(USER_DATA_KEY_PROTOKOLL_GROUP_ORDER, dedupeGroupNames(availableProtokollGroups));
  }, [availableProtokollGroups, sessionUserId, isAdmin, accessStatus, isGroupOrderHydrated, saveUserPrivateData]);

  useEffect(() => {
    setIsVorlagenSubsectionOrderHydrated(false);
    if (!sessionUserId || !supabase) {
      const localOrder = normalizeOrderMap(readJsonStorage(userVorlagenSubsectionOrderStorageKey, {}));
      setVorlagenSubsectionOrderByMod(localOrder);
      setIsVorlagenSubsectionOrderHydrated(true);
      return;
    }
    if (!isAdmin && accessStatus !== "approved") {
      setIsVorlagenSubsectionOrderHydrated(true);
      return;
    }

    let isCancelled = false;

    const hydrateVorlagenSubsectionOrder = async () => {
      const privateOrder = await loadUserPrivateData(
        USER_DATA_KEY_VORLAGEN_SUBSECTION_ORDER,
        userVorlagenSubsectionOrderStorageKey,
        {}
      );
      if (isCancelled) return;

      const normalizedOrder = normalizeOrderMap(privateOrder.value);
      setVorlagenSubsectionOrderByMod(normalizedOrder);
      writeJsonStorage(userVorlagenSubsectionOrderStorageKey, normalizedOrder);
      setIsVorlagenSubsectionOrderHydrated(true);
    };

    void hydrateVorlagenSubsectionOrder();
    return () => {
      isCancelled = true;
    };
  }, [
    accessStatus,
    isAdmin,
    loadUserPrivateData,
    sessionUserId,
    userVorlagenSubsectionOrderStorageKey,
  ]);

  useEffect(() => {
    if (!isVorlagenSubsectionOrderHydrated) return;
    const normalizedOrder = normalizeOrderMap(vorlagenSubsectionOrderByMod);
    writeJsonStorage(userVorlagenSubsectionOrderStorageKey, normalizedOrder);
    if (!sessionUserId || !supabase) return;
    if (!isAdmin && accessStatus !== "approved") return;
    void saveUserPrivateData(USER_DATA_KEY_VORLAGEN_SUBSECTION_ORDER, normalizedOrder);
  }, [
    accessStatus,
    isAdmin,
    isVorlagenSubsectionOrderHydrated,
    saveUserPrivateData,
    sessionUserId,
    userVorlagenSubsectionOrderStorageKey,
    vorlagenSubsectionOrderByMod,
  ]);

  useEffect(() => {
    setIsVorlagenCategoryHydrated(false);
    if (!sessionUserId || !supabase) {
      const localCategories = readJsonStorage(userVorlagenCategoryStorageKey, {});
      setVorlagenCategoryById(localCategories && typeof localCategories === "object" && !Array.isArray(localCategories) ? localCategories : {});
      setIsVorlagenCategoryHydrated(true);
      return;
    }
    if (!isAdmin && accessStatus !== "approved") {
      setIsVorlagenCategoryHydrated(true);
      return;
    }

    let isCancelled = false;

    const hydrateVorlagenCategories = async () => {
      const privateCategories = await loadUserPrivateData(
        USER_DATA_KEY_VORLAGEN_CATEGORY,
        userVorlagenCategoryStorageKey,
        {}
      );
      if (isCancelled) return;

      const value = privateCategories.value && typeof privateCategories.value === "object" && !Array.isArray(privateCategories.value)
        ? privateCategories.value
        : {};
      setVorlagenCategoryById(value);
      writeJsonStorage(userVorlagenCategoryStorageKey, value);
      setIsVorlagenCategoryHydrated(true);
    };

    void hydrateVorlagenCategories();
    return () => {
      isCancelled = true;
    };
  }, [
    accessStatus,
    isAdmin,
    loadUserPrivateData,
    sessionUserId,
    userVorlagenCategoryStorageKey,
  ]);

  useEffect(() => {
    if (!isVorlagenCategoryHydrated) return;
    writeJsonStorage(userVorlagenCategoryStorageKey, vorlagenCategoryById);
    if (!sessionUserId || !supabase) return;
    if (!isAdmin && accessStatus !== "approved") return;
    void saveUserPrivateData(USER_DATA_KEY_VORLAGEN_CATEGORY, vorlagenCategoryById);
  }, [
    accessStatus,
    isAdmin,
    isVorlagenCategoryHydrated,
    saveUserPrivateData,
    sessionUserId,
    userVorlagenCategoryStorageKey,
    vorlagenCategoryById,
  ]);

  useEffect(() => {
    setIsVorlagenEmptyCategoriesHydrated(false);
    if (!sessionUserId || !supabase) {
      const localCategories = normalizeStringArrayMap(readJsonStorage(userVorlagenEmptyCategoriesStorageKey, {}));
      setVorlagenEmptyCategoriesBySubsection(localCategories);
      setIsVorlagenEmptyCategoriesHydrated(true);
      return;
    }
    if (!isAdmin && accessStatus !== "approved") {
      setIsVorlagenEmptyCategoriesHydrated(true);
      return;
    }

    let isCancelled = false;

    const hydrateVorlagenEmptyCategories = async () => {
      const privateCategories = await loadUserPrivateData(
        USER_DATA_KEY_VORLAGEN_EMPTY_CATEGORIES,
        userVorlagenEmptyCategoriesStorageKey,
        {}
      );
      if (isCancelled) return;

      const value = normalizeStringArrayMap(privateCategories.value);
      setVorlagenEmptyCategoriesBySubsection(value);
      writeJsonStorage(userVorlagenEmptyCategoriesStorageKey, value);
      setIsVorlagenEmptyCategoriesHydrated(true);
    };

    void hydrateVorlagenEmptyCategories();
    return () => {
      isCancelled = true;
    };
  }, [
    accessStatus,
    isAdmin,
    loadUserPrivateData,
    sessionUserId,
    userVorlagenEmptyCategoriesStorageKey,
  ]);

  useEffect(() => {
    if (!isVorlagenEmptyCategoriesHydrated) return;
    const normalized = normalizeStringArrayMap(vorlagenEmptyCategoriesBySubsection);
    writeJsonStorage(userVorlagenEmptyCategoriesStorageKey, normalized);
    if (!sessionUserId || !supabase) return;
    if (!isAdmin && accessStatus !== "approved") return;
    void saveUserPrivateData(USER_DATA_KEY_VORLAGEN_EMPTY_CATEGORIES, normalized);
  }, [
    accessStatus,
    isAdmin,
    isVorlagenEmptyCategoriesHydrated,
    saveUserPrivateData,
    sessionUserId,
    userVorlagenEmptyCategoriesStorageKey,
    vorlagenEmptyCategoriesBySubsection,
  ]);

  const visibleVorlagenGroups = useMemo(() => (
    availableVorlagenGroups.filter((group) => (
      !INTERNAL_VORLAGEN_GROUPS.includes(group) &&
      !HIDDEN_VORLAGEN_GROUPS.includes(group)
    ))
  ), [availableVorlagenGroups]);

  const availableVorlagenCreateGroups = useMemo(() => sortGroupNames([
    ...DEFAULT_VORLAGEN_CREATE_GROUPS,
    ...visibleVorlagenGroups.filter((group) => !["MRT 1,5", "MRT 3"].includes(group)),
  ], DEFAULT_VORLAGEN_CREATE_GROUPS), [visibleVorlagenGroups]);

  const persistGroupOrderNow = useCallback((dataKey, nextOrder) => {
    const normalizedOrder = dedupeGroupNames(nextOrder);
    if (!sessionUserId || !supabase) return;
    if (!isAdmin && accessStatus !== "approved") return;
    void saveUserPrivateData(dataKey, normalizedOrder);
  }, [accessStatus, isAdmin, saveUserPrivateData, sessionUserId]);

  const persistVorlagenSubsectionOrderNow = useCallback((nextOrderByMod) => {
    const normalizedOrder = normalizeOrderMap(nextOrderByMod);
    writeJsonStorage(userVorlagenSubsectionOrderStorageKey, normalizedOrder);
    if (!sessionUserId || !supabase) return;
    if (!isAdmin && accessStatus !== "approved") return;
    void saveUserPrivateData(USER_DATA_KEY_VORLAGEN_SUBSECTION_ORDER, normalizedOrder);
  }, [accessStatus, isAdmin, saveUserPrivateData, sessionUserId, userVorlagenSubsectionOrderStorageKey]);

  const updateVorlageCategory = useCallback((vorlageOrId, nextCategory) => {
    const isVorlageObject = vorlageOrId && typeof vorlageOrId === "object";
    const id = String(isVorlageObject ? vorlageOrId.id : vorlageOrId || "");
    const stableKey = isVorlageObject ? createVorlageCategoryKey(vorlageOrId) : "";
    const assignmentKey = isVorlageObject ? createVorlageAssignmentCategoryKey(vorlageOrId) : "";
    if (!id && !stableKey && !assignmentKey) return;
    const normalizedCategory = String(nextCategory || "").trim();
    setVorlagenCategoryById((prev) => {
      const next = { ...prev };
      if (normalizedCategory) {
        if (id) next[id] = normalizedCategory;
        if (stableKey) next[stableKey] = normalizedCategory;
        if (assignmentKey) next[assignmentKey] = normalizedCategory;
      } else {
        if (id) delete next[id];
        if (stableKey) delete next[stableKey];
        if (assignmentKey) delete next[assignmentKey];
      }
      writeJsonStorage(userVorlagenCategoryStorageKey, next);
      if (sessionUserId && supabase && (isAdmin || accessStatus === "approved")) {
        void saveUserPrivateData(USER_DATA_KEY_VORLAGEN_CATEGORY, next);
      }
      return next;
    });
    setVorlagen((prev) => {
      const nextVorlagen = (Array.isArray(prev) ? prev : []).map((vorlage) => {
        const isMatch =
          (id && String(vorlage?.id || "") === id) ||
          (stableKey && createVorlageCategoryKey(vorlage) === stableKey) ||
          (assignmentKey && createVorlageAssignmentCategoryKey(vorlage) === assignmentKey);

        if (!isMatch) return vorlage;
        if (!normalizedCategory) {
          const { category, ...rest } = vorlage;
          return rest;
        }
        return { ...vorlage, category: normalizedCategory };
      });
      return nextVorlagen;
    });
  }, [accessStatus, isAdmin, saveUserPrivateData, sessionUserId, userVorlagenCategoryStorageKey]);

  const addVorlagenEmptyCategory = useCallback((subsectionKey, category) => {
    const normalizedKey = String(subsectionKey || "").trim();
    const normalizedCategory = normalizeGroupName(category);
    if (!normalizedKey || !normalizedCategory) return;

    setVorlagenEmptyCategoriesBySubsection((prev) => {
      const next = {
        ...prev,
        [normalizedKey]: dedupeGroupNames([...(prev[normalizedKey] || []), normalizedCategory]),
      };
      const normalized = normalizeStringArrayMap(next);
      writeJsonStorage(userVorlagenEmptyCategoriesStorageKey, normalized);
      if (sessionUserId && supabase && (isAdmin || accessStatus === "approved")) {
        void saveUserPrivateData(USER_DATA_KEY_VORLAGEN_EMPTY_CATEGORIES, normalized);
      }
      return normalized;
    });
  }, [accessStatus, isAdmin, saveUserPrivateData, sessionUserId, userVorlagenEmptyCategoriesStorageKey]);

  const vorlagenGruppiert = useMemo(() => availableVorlagenGroups.reduce((acc, modGroup) => {
    if (modGroup === "MRT 1,5") {
      acc[modGroup] = vorlagenForMod(modGroup).filter((prompt) => prompt.mod === "MRT 1,5");
      return acc;
    }

    if (modGroup === "MRT 3") {
      acc[modGroup] = vorlagenForMod(modGroup).filter((prompt) => prompt.mod === "MRT 3");
      return acc;
    }

    acc[modGroup] = vorlagenForMod(modGroup);
    return acc;
  }, {}), [availableVorlagenGroups, vorlagenForMod]);

  const currentModGroup = fields.modalitat === "CT" ? "CT" : "MRT";
  const availableSections = Object.keys(befundbeispiele[currentModGroup] || {});
  const filterBefundbeispiele = useCallback((list) => {
    if (!deferredBefundFilterText) return list;
    const q = deferredBefundFilterText.toLowerCase();
    return list.filter((t) => (
      (t.name || "").toLowerCase().includes(q) ||
      (t.category || "").toLowerCase().includes(q) ||
      richTextToPlainText(t.befund).toLowerCase().includes(q) ||
      richTextToPlainText(t.beurteilung).toLowerCase().includes(q)
    ));
  }, [deferredBefundFilterText]);

  const filterProtokolle = useCallback((list) => {
    if (!deferredProtokollFilterText) return list;
    const q = deferredProtokollFilterText.toLowerCase();
    return list.filter((t) => (
      (t.name || "").toLowerCase().includes(q) ||
      (t.category || "").toLowerCase().includes(q) ||
      richTextToPlainText(t.befund).toLowerCase().includes(q) ||
      richTextToPlainText(t.beurteilung).toLowerCase().includes(q)
    ));
  }, [deferredProtokollFilterText]);

  const inputFieldStyle = useMemo(() => ({ background: themeColors.bgSecondary, border: `1.5px solid ${themeColors.borderSecondary}`, borderRadius: "8px", padding: "7px 10px", fontSize: 13, color: themeColors.text, fontFamily: "inherit" }), [themeColors]);
  const fieldLabelStyle = useMemo(() => ({ fontSize: 10, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: themeColors.textSecondary, marginBottom: 5, display: "block" }), [themeColors]);

  const modBadge = useCallback((m) => {
    const modObj = modalities.find(mod => mod.name === m);
    if (modObj) {
      const isDark = isDarkTheme;
      return {
        display: "inline-block", padding: "2px 10px", borderRadius: 99, fontSize: 11, fontWeight: 600,
        background: isDark ? modObj.color_bg_dark : modObj.color_bg_light,
        color: isDark ? modObj.color_text_dark : modObj.color_text_light,
      };
    }
    // Fallback
    return {
      display: "inline-block", padding: "2px 10px", borderRadius: 99, fontSize: 11, fontWeight: 600,
      background: m === "CT" ? (isDarkTheme ? "#2d2010" : "#fffbeb") : m === "Sonstiges" ? (isDarkTheme ? "#0d2d1a" : "#f0fff4") : m === "Volumetrie" ? (isDarkTheme ? "#2d1a2d" : "#fdf4ff") : (isDarkTheme ? "#0d2137" : "#ebf8ff"),
      color: m === "CT" ? (isDarkTheme ? "#f6ad55" : "#c05621") : m === "Sonstiges" ? (isDarkTheme ? "#48bb78" : "#276749") : m === "Volumetrie" ? (isDarkTheme ? "#d6bcfa" : "#805ad5") : (isDarkTheme ? "#63b3ed" : "#2b6cb0"),
    };
  }, [isDarkTheme, modalities]);

  const moveVorlagenGroup = (groupName, direction) => {
    const currentOrder = [...visibleVorlagenGroups];
    const index = currentOrder.findIndex((group) => group === groupName);
    if (index === -1) return;

    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= currentOrder.length) return;

    [currentOrder[index], currentOrder[targetIndex]] = [currentOrder[targetIndex], currentOrder[index]];
    updateVorlagenGroupOrder(currentOrder);
    persistGroupOrderNow(USER_DATA_KEY_VORLAGEN_GROUP_ORDER, currentOrder);
    showStatus("Sektion verschoben ✓");
  };

  const moveBefundGroup = (groupName, direction) => {
    const currentOrder = [...availableBefundGroups];
    const index = currentOrder.findIndex((group) => group === groupName);
    if (index === -1) return;

    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= currentOrder.length) return;

    [currentOrder[index], currentOrder[targetIndex]] = [currentOrder[targetIndex], currentOrder[index]];
    updateBefundGroupOrder(currentOrder);
    persistGroupOrderNow(USER_DATA_KEY_BEFUND_GROUP_ORDER, currentOrder);
    showStatus("Sektion verschoben ✓");
  };

  const moveProtokollGroup = (groupName, direction) => {
    const currentOrder = [...availableProtokollGroups];
    const index = currentOrder.findIndex((group) => group === groupName);
    if (index === -1) return;

    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= currentOrder.length) return;

    [currentOrder[index], currentOrder[targetIndex]] = [currentOrder[targetIndex], currentOrder[index]];
    updateProtokollGroupOrder(currentOrder);
    persistGroupOrderNow(USER_DATA_KEY_PROTOKOLL_GROUP_ORDER, currentOrder);
    showStatus("Sektion verschoben ✓");
  };

  const updateVorlagenSubsectionOrder = useCallback((updater) => {
    setVorlagenSubsectionOrderByMod((prev) => {
      const rawNext = typeof updater === "function" ? updater(prev) : updater;
      const nextOrder = normalizeOrderMap(rawNext);
      persistVorlagenSubsectionOrderNow(nextOrder);
      return nextOrder;
    });
  }, [persistVorlagenSubsectionOrderNow]);

  const moveVorlagenSubsection = useCallback((modGroup, subsection, direction, visibleSubsections = []) => {
    const currentOrder = dedupeGroupNames(
      (vorlagenSubsectionOrderByMod[modGroup]?.length ? vorlagenSubsectionOrderByMod[modGroup] : visibleSubsections)
    );
    const visibleOrder = dedupeGroupNames(visibleSubsections);
    const mergedOrder = dedupeGroupNames([...currentOrder, ...visibleOrder]);
    const index = mergedOrder.findIndex((entry) => entry === subsection);
    if (index === -1) return;

    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= mergedOrder.length) return;

    [mergedOrder[index], mergedOrder[targetIndex]] = [mergedOrder[targetIndex], mergedOrder[index]];
    updateVorlagenSubsectionOrder((prev) => ({
      ...prev,
      [modGroup]: mergedOrder,
    }));
    showStatus("Untersektion verschoben ✓");
  }, [showStatus, updateVorlagenSubsectionOrder, vorlagenSubsectionOrderByMod]);

  const renameVorlagenSubsectionOrder = useCallback((modGroup, previousName, nextName) => {
    updateVorlagenSubsectionOrder((prev) => {
      const order = prev[modGroup];
      if (!Array.isArray(order)) return prev;

      return {
        ...prev,
        [modGroup]: dedupeGroupNames(order.map((entry) => (entry === previousName ? nextName : entry))),
      };
    });
  }, [updateVorlagenSubsectionOrder]);

  const deleteVorlagenSubsectionOrder = useCallback((modGroup, subsection) => {
    updateVorlagenSubsectionOrder((prev) => {
      const order = prev[modGroup];
      if (!Array.isArray(order)) return prev;

      return {
        ...prev,
        [modGroup]: order.filter((entry) => entry !== subsection),
      };
    });
  }, [updateVorlagenSubsectionOrder]);

  const {
    createVorlagenGroup,
    createBefundGroup,
    createProtokollGroup,
    deleteVorlagenGroup,
    deleteBefundGroup,
    deleteProtokollGroup,
  } = useGroupManagement({
    normalizeGroupName,
    findExistingGroupName,
    sortGroupNames,
    DEFAULT_VORLAGEN_GROUPS,
    DEFAULT_BEFUNDBEISPIELE_GROUPS,
    DEFAULT_PROTOKOLLE_GROUPS,
    availableVorlagenGroups,
    availableBefundGroups,
    availableProtokollGroups,
    customVorlagenGroups,
    customBefundGroups,
    customProtokollGroups,
    setCustomVorlagenGroups,
    setCustomBefundGroups,
    setCustomProtokollGroups,
    persistCustomVorlagenGroups,
    persistCustomBefundGroups,
    persistCustomProtokollGroups,
    updateVorlagenGroupOrder,
    updateBefundGroupOrder,
    updateProtokollGroupOrder,
    showStatus,
    newVorlagenGroupName,
    newBefundGroupName,
    newProtokollGroupName,
    setNewPromptMod,
    setNewPromptMrtTarget,
    setExpandedVorlagenGroups,
    setNewVorlagenGroupName,
    setNewPromptSubsection,
    setNewPromptFormOpen,
    setExpandedSections,
    setNewBefundGroupName,
    setNewTplMod,
    setNewSectionMod,
    setNewTplSection,
    setNewSectionName,
    setNewTplFormOpen,
    setExpandedProtokollSections: setProtokollExpandedSections,
    setNewProtokollGroupName,
    setNewProtokollMod,
    setNewProtokollSectionMod,
    setNewProtokollSection,
    setNewProtokollSectionName,
    setNewProtokollFormOpen,
    vorlagenGruppiert,
    isAdmin,
    deleteUserPromptInTable,
    formatSupabaseError,
    vorlagen,
    setVorlagen,
    persistUserVorlagen,
    writeJsonStorage,
    userVorlagenStorageKey,
    newPromptMod,
    supabase,
    tableNames,
    loadAllVorlagen,
    befundbeispiele,
    cloneData,
    setBefundbeispiele,
    persistUserBefundbeispiele,
    newTplMod,
    newSectionMod,
    loadAllBefundbeispiele,
    protokolle,
    setProtokolle,
    persistUserProtokolle,
    newProtokollMod,
    newProtokollSectionMod,
    loadAllProtokolle,
  });

  const {
    confirmSaveTemplate,
    confirmSaveNewTemplate,
    confirmCreateSection,
    confirmDeleteTemplate,
    doDeleteTemplate,
    confirmDeleteSection,
    doDeleteSection,
    moveTemplate,
    moveSection,
    renameBefundSection,
    deleteBefundSection,
    saveBefundbeispielInline,
  } = useBefundbeispieleOperations({
    isAdmin,
    befundbeispiele,
    setBefundbeispiele,
    persistUserBefundbeispiele,
    cloneData,
    supabase,
    tableNames,
    loadAllBefundbeispiele,
    showStatus,
    formatSupabaseError,
    createTimeBasedId,
    templateDraftName,
    templateDraftSection,
    editingId,
    setTemplateDraftName,
    setTemplateDraftSection,
    setEditingId,
    fields,
    saveTemplateInFlightRef,
    setIsSavingTemplate,
    newTplSection,
    newTplCategory,
    newSectionName,
    newSectionMod,
    newTplName,
    newTplBefund,
    newTplBeurteilung,
    newTplMod,
    setNewTplFormOpen,
    setNewTplMod,
    setNewTplSection,
    setNewTplCategory,
    setNewSectionName,
    setNewTplName,
    setNewTplBefund,
    setNewTplBeurteilung,
    saveNewTemplateInFlightRef,
    setIsSavingNewTemplate,
    normalizeGroupName,
    findExistingGroupName,
    sortGroupNames,
    defaultBefundGroups: DEFAULT_BEFUNDBEISPIELE_GROUPS,
    customBefundGroups,
    setCustomBefundGroups,
    persistCustomBefundGroups,
    updateBefundGroupOrder,
    availableBefundGroups,
    setExpandedSections,
    setNewSectionMod,
    deleteConfirm,
    setDeleteConfirm,
    sectionDeleteConfirm,
    setSectionDeleteConfirm,
  });

  const {
    confirmSaveNewTemplate: confirmSaveNewProtokoll,
    confirmCreateSection: confirmCreateProtokollSection,
    confirmDeleteTemplate: confirmDeleteProtokoll,
    confirmDeleteSection: confirmDeleteProtokollSection,
    moveTemplate: moveProtokoll,
    moveSection: moveProtokollSection,
    renameBefundSection: renameProtokollSection,
    deleteBefundSection: deleteProtokollSection,
    saveBefundbeispielInline: saveProtokollInline,
  } = useBefundbeispieleOperations({
    isAdmin,
    befundbeispiele: protokolle,
    setBefundbeispiele: setProtokolle,
    persistUserBefundbeispiele: persistUserProtokolle,
    cloneData,
    supabase,
    tableNames,
    loadAllBefundbeispiele: loadAllProtokolle,
    showStatus,
    formatSupabaseError,
    createTimeBasedId,
    templateDraftName,
    templateDraftSection,
    editingId,
    setTemplateDraftName,
    setTemplateDraftSection,
    setEditingId,
    fields,
    saveTemplateInFlightRef,
    setIsSavingTemplate,
    newTplSection: newProtokollSection,
    newTplCategory: newProtokollCategory,
    newSectionName: newProtokollSectionName,
    newSectionMod: newProtokollSectionMod,
    newTplName: newProtokollName,
    newTplBefund: newProtokollBefund,
    newTplBeurteilung: newProtokollBeurteilung,
    newTplMod: newProtokollMod,
    setNewTplFormOpen: setNewProtokollFormOpen,
    setNewTplMod: setNewProtokollMod,
    setNewTplSection: setNewProtokollSection,
    setNewTplCategory: setNewProtokollCategory,
    setNewSectionName: setNewProtokollSectionName,
    setNewTplName: setNewProtokollName,
    setNewTplBefund: setNewProtokollBefund,
    setNewTplBeurteilung: setNewProtokollBeurteilung,
    saveNewTemplateInFlightRef,
    setIsSavingNewTemplate: setIsSavingNewProtokoll,
    normalizeGroupName,
    findExistingGroupName,
    sortGroupNames,
    defaultBefundGroups: DEFAULT_PROTOKOLLE_GROUPS,
    customBefundGroups: customProtokollGroups,
    setCustomBefundGroups: setCustomProtokollGroups,
    persistCustomBefundGroups: persistCustomProtokollGroups,
    updateBefundGroupOrder: updateProtokollGroupOrder,
    availableBefundGroups: availableProtokollGroups,
    setExpandedSections: setProtokollExpandedSections,
    setNewSectionMod: setNewProtokollSectionMod,
    deleteConfirm: protokollDeleteConfirm,
    setDeleteConfirm: setProtokollDeleteConfirm,
    sectionDeleteConfirm: protokollSectionDeleteConfirm,
    setSectionDeleteConfirm: setProtokollSectionDeleteConfirm,
    tableNameKey: "protokolle",
    itemLabel: "Protokoll",
  });

  const modColor = useCallback((m) => {
    const modObj = modalities.find(mod => mod.name === m);
    if (modObj) {
      return {
        bg: isDarkTheme ? modObj.color_bg_dark : modObj.color_bg_light,
        color: isDarkTheme ? modObj.color_text_dark : modObj.color_text_light,
      };
    }
    // Fallback ako modality nije pronađen
    return m === "CT"
      ? { bg: isDarkTheme ? "#2d2010" : "#fffbeb", color: isDarkTheme ? "#f6ad55" : "#c05621" }
      : m === "Sonstiges"
      ? { bg: isDarkTheme ? "#0d2d1a" : "#f0fff4", color: isDarkTheme ? "#48bb78" : "#276749" }
      : m === "Volumetrie"
      ? { bg: isDarkTheme ? "#2d1a2d" : "#fdf4ff", color: isDarkTheme ? "#d6bcfa" : "#805ad5" }
        : m === "Modalitäten"
        ? { bg: isDarkTheme ? "#1f2437" : "#eef2ff", color: isDarkTheme ? "#a5b4fc" : "#4338ca" }
        : { bg: isDarkTheme ? "#0d2137" : "#ebf8ff", color: isDarkTheme ? "#63b3ed" : "#2b6cb0" };
      }, [isDarkTheme, modalities]);

  const Btn = useCallback((props) => <ActionButton {...props} c={themeColors} />, [themeColors]);

  const DropItem = (props) => <DropdownItem {...props} c={themeColors} />;
  const headerLogoSrc = `${process.env.PUBLIC_URL}/RadVentura%20192x192.png`;

  if (!session) {
    return (
      <LoginPage
        c={themeColors}
        inp={inputFieldStyle}
        dark={isDarkTheme}
        onToggleDark={() => setIsDarkTheme((d) => !d)}
        supabase={supabase}
      />
    );
  }

  if (!isAdmin && accessStatus === "checking") {
    return (
      <div style={{ minHeight: "100vh", background: themeColors.bg, color: themeColors.text, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ width: "100%", maxWidth: 460, background: themeColors.bgSecondary, border: `1px solid ${themeColors.border}`, borderRadius: 14, padding: 22, boxSizing: "border-box" }}>
          <div style={{ fontWeight: 700, fontSize: 22, marginBottom: 10 }}>RadVentura</div>
          <div style={{ fontSize: 14, marginBottom: 8 }}>Zugang wird geprüft...</div>
          <div style={{ fontSize: 12, color: themeColors.textSecondary }}>Ihre Freigabe wird geladen. Bitte einen Moment warten.</div>
        </div>
      </div>
    );
  }

  if (!isAdmin && isAccessControlAvailable && accessStatus !== "approved") {
    const isRejected = accessStatus === "rejected";
    return (
      <div style={{ minHeight: "100vh", background: themeColors.bg, color: themeColors.text, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ width: "100%", maxWidth: 520, background: themeColors.bgSecondary, border: `1px solid ${themeColors.border}`, borderRadius: 14, padding: 22, boxSizing: "border-box" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 22 }}>RadVentura</div>
              <div style={{ fontSize: 12, color: themeColors.textSecondary }}>Zugriff geschützt</div>
            </div>
            <button onClick={() => supabase.auth.signOut()} style={{ background: isDarkTheme ? "#2d1515" : "#fff0f0", color: isDarkTheme ? "#fc8181" : "#cc3333", border: `1px solid ${isDarkTheme ? "#4a2020" : "#ffcccc"}`, borderRadius: 20, padding: "5px 12px", cursor: "pointer", fontSize: 12 }}>
              Abmelden
            </button>
          </div>

          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
            {isRejected ? "Zugang derzeit nicht freigegeben" : "Freigabe ausstehend"}
          </div>
          <div style={{ fontSize: 13, color: themeColors.textSecondary, lineHeight: 1.6 }}>
            {isRejected
              ? "Ihr Konto wurde erstellt, ist aber aktuell nicht zur Nutzung freigegeben. Bitte wenden Sie sich an den Administrator."
              : "Ihr Konto wurde erstellt und bestätigt. Der Zugriff wird freigeschaltet, sobald ein Administrator Ihre Anfrage genehmigt hat."}
          </div>
          {accessRecord?.email && (
            <div style={{ marginTop: 12, fontSize: 12, color: themeColors.textSecondary }}>
              Konto: {accessRecord.email}
            </div>
          )}

          <div style={{ marginTop: 18, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              onClick={() => void syncOwnAccessRequest(session)}
              style={{ background: themeColors.bg, color: themeColors.text, border: `1px solid ${themeColors.border}`, borderRadius: 8, padding: "8px 14px", cursor: "pointer", fontSize: 13 }}
            >
              Status aktualisieren
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif", minHeight: "100vh", paddingBottom: "3rem", background: themeColors.bg, color: themeColors.text, transition: "background .2s, color .2s" }}>
      <style>{`
        @keyframes blink{0%,100%{opacity:1}50%{opacity:.2}}
        @keyframes spin{to{transform:rotate(360deg)}}
      `}</style>

      <AppHeader
        c={themeColors}
        dark={isDarkTheme}
        aiProvider={aiProvider}
        setAiProvider={setAiProvider}
        resetApp={resetApp}
        headerLogoSrc={headerLogoSrc}
        session={session}
        isAdmin={isAdmin}
        isDemoUser={isDemoUser}
        onSignOut={() => supabase.auth.signOut()}
        setIsDarkTheme={setIsDarkTheme}
        visibleTabs={visibleTabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      {activeTab === "befundung" && (
        <EditorTab
          c={themeColors}
          dark={isDarkTheme}
          lbl={fieldLabelStyle}
          inp={inputFieldStyle}
          fields={fields}
          modOpen={isModalityDropdownOpen}
          regionOpen={isRegionDropdownOpen}
          geschlechtOpen={isGenderDropdownOpen}
          setModOpen={setIsModalityDropdownOpen}
          setRegionOpen={setIsRegionDropdownOpen}
          setGeschlechtOpen={setIsGenderDropdownOpen}
          showStatus={showStatus}
          selectModality={selectModality}
          selectRegion={selectRegion}
          selectGeschlecht={selectGeschlecht}
          vorlagenForMod={vorlagenForMod}
          regionOptionsForMod={regionOptionsForMod}
          regionTreeForMod={regionTreeForMod}
          selectedRegionLabel={selectedRegionLabel}
          modColor={modColor}
          copyText={copyText}
          undoField={undoField}
          redoField={redoField}
          fieldHistory={fieldHistory}
          fieldFuture={fieldFuture}
          updateField={updateField}
          updateKlinischeAngaben={updateKlinischeAngaben}
          updateFragestellung={updateFragestellung}
          aiQuickPrompts={aiQuickPrompts}
          aiBeurteilungQuickPrompts={aiBeurteilungQuickPrompts}
          aiPromptSyncStatus={aiPromptSyncStatus}
          canDeleteAiPrompts={!!session && !!supabase}
          saveAiQuickPrompt={saveAiQuickPrompt}
          saveAiBeurteilungQuickPrompt={saveAiBeurteilungQuickPrompt}
          deleteAiQuickPrompt={deleteAiQuickPrompt}
          deleteAiBeurteilungQuickPrompt={deleteAiBeurteilungQuickPrompt}
          aiBefundPrompt={aiBefundPrompt}
          aiBeurteilungPrompt={aiBeurteilungPrompt}
          beurteilungGeneratePrompt={beurteilungGeneratePrompt}
          beurteilungStyleProfile={beurteilungStyleProfile}
          setAiBefundPrompt={setAiBefundPrompt}
          setAiBeurteilungPrompt={setAiBeurteilungPrompt}
          setBeurteilungGeneratePrompt={setBeurteilungGeneratePrompt}
          setBeurteilungStyleProfile={setBeurteilungStyleProfile}
          onSaveBeurteilungGeneratePrompt={saveBeurteilungGeneratePromptHandler}
          aiBefundResponse={aiBefundResponse}
          aiBefundHistory={aiBefundHistory}
          aiBefundLastAction={aiBefundLastAction}
          aiBefundSelection={aiBefundSelection}
          updateAiBefundSelection={updateAiBefundSelection}
          isAiBefundLoading={isAiBefundLoading}
          isAiBeurteilungLoading={isAiBeurteilungLoading}
          runAiBefundAssistant={runAiBefundAssistant}
          runAiBeurteilungAssistant={runAiBeurteilungAssistant}
          applyAiBefundResponse={applyAiBefundResponse}
          restoreAiBefundHistoryEntry={restoreAiBefundHistoryEntry}
          deleteAiBefundHistoryEntry={deleteAiBefundHistoryEntry}
          clearAiBefundHistory={clearAiBefundHistory}
          Btn={Btn}
          DropItem={DropItem}
          InlineDropdown={InlineDropdown}
          isGenerating={isGenerating}
          generateBeurteilung={generateBeurteilung}
          currentModGroup={currentModGroup}
          tplName={templateDraftName}
          setTplName={setTemplateDraftName}
          tplSection={templateDraftSection}
          setTplSection={setTemplateDraftSection}
          availableSections={availableSections}
          confirmSaveTemplate={confirmSaveTemplate}
          isSavingTemplate={isSavingTemplate}
          editingId={editingId}
          setEditingId={setEditingId}
          resetApp={resetApp}
          modalities={modalityOptions}
          modalityTree={modalityTree}
          lastAutosaveAt={lastAutosaveAt}
          reportHistory={reportHistory}
          saveCurrentToHistory={saveCurrentToHistory}
          restoreFromHistory={restoreFromHistory}
          deleteHistoryEntry={deleteHistoryEntry}
          clearHistory={clearHistory}
        />
      )}

      {activeTab === "befundbeispiele" && (
        <BefundbeispieleTab
          c={themeColors}
          dark={isDarkTheme}
          lbl={fieldLabelStyle}
          inp={inputFieldStyle}
          filterText={filterText}
          setFilterText={setFilterText}
          newSectionMod={newSectionMod}
          setNewSectionMod={setNewSectionMod}
          newSectionName={newSectionName}
          setNewSectionName={setNewSectionName}
          befundGroupOptions={availableBefundGroups}
          newBefundGroupName={newBefundGroupName}
          setNewBefundGroupName={setNewBefundGroupName}
          onCreateBefundGroup={createBefundGroup}
          onMoveBefundGroup={moveBefundGroup}
          onDeleteBefundGroup={deleteBefundGroup}
          confirmCreateSection={confirmCreateSection}
          newTplFormOpen={newTplFormOpen}
          setNewTplFormOpen={setNewTplFormOpen}
          newTplMod={newTplMod}
          setNewTplMod={setNewTplMod}
          newTplSection={newTplSection}
          setNewTplSection={setNewTplSection}
          newTplCategory={newTplCategory}
          setNewTplCategory={setNewTplCategory}
          newTplName={newTplName}
          setNewTplName={setNewTplName}
          newTplBefund={newTplBefund}
          setNewTplBefund={setNewTplBefund}
          newTplBeurteilung={newTplBeurteilung}
          setNewTplBeurteilung={setNewTplBeurteilung}
          confirmSaveNewTemplate={confirmSaveNewTemplate}
          isSavingNewTemplate={isSavingNewTemplate}
          befundbeispiele={befundbeispiele}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
          filterBefundbeispiele={filterBefundbeispiele}
          modBadge={modBadge}
          modColor={modColor}
          moveSection={moveSection}
          confirmDeleteSection={confirmDeleteSection}
          loadBefundbeispiel={loadBefundbeispiel}
          moveTemplate={moveTemplate}
          confirmDeleteTemplate={confirmDeleteTemplate}
          onSavePreviewTemplate={saveBefundbeispielInline}
          onRenameBefundSection={renameBefundSection}
          onDeleteBefundSection={deleteBefundSection}
          showStatus={showStatus}
          Btn={Btn}
        />
      )}

      {activeTab === "protokolle" && (
        <BefundbeispieleTab
          c={themeColors}
          dark={isDarkTheme}
          lbl={fieldLabelStyle}
          inp={inputFieldStyle}
          labels={{
            itemLabel: "Protokoll",
            itemLabelPlural: "Protokolle",
            searchPlaceholder: "Protokolle durchsuchen...",
            createTitle: "Neues Protokoll direkt erstellen",
            namePlaceholder: "Protokoll-Name...",
          }}
          filterText={protokollFilterText}
          setFilterText={setProtokollFilterText}
          newSectionMod={newProtokollSectionMod}
          setNewSectionMod={setNewProtokollSectionMod}
          newSectionName={newProtokollSectionName}
          setNewSectionName={setNewProtokollSectionName}
          befundGroupOptions={availableProtokollGroups}
          newBefundGroupName={newProtokollGroupName}
          setNewBefundGroupName={setNewProtokollGroupName}
          onCreateBefundGroup={createProtokollGroup}
          onMoveBefundGroup={moveProtokollGroup}
          onDeleteBefundGroup={deleteProtokollGroup}
          confirmCreateSection={confirmCreateProtokollSection}
          newTplFormOpen={newProtokollFormOpen}
          setNewTplFormOpen={setNewProtokollFormOpen}
          newTplMod={newProtokollMod}
          setNewTplMod={setNewProtokollMod}
          newTplSection={newProtokollSection}
          setNewTplSection={setNewProtokollSection}
          newTplCategory={newProtokollCategory}
          setNewTplCategory={setNewProtokollCategory}
          newTplName={newProtokollName}
          setNewTplName={setNewProtokollName}
          newTplBefund={newProtokollBefund}
          setNewTplBefund={setNewProtokollBefund}
          newTplBeurteilung={newProtokollBeurteilung}
          setNewTplBeurteilung={setNewProtokollBeurteilung}
          confirmSaveNewTemplate={confirmSaveNewProtokoll}
          isSavingNewTemplate={isSavingNewProtokoll}
          befundbeispiele={protokolle}
          expandedSections={protokollExpandedSections}
          toggleSection={toggleProtokollSection}
          filterBefundbeispiele={filterProtokolle}
          modBadge={modBadge}
          modColor={modColor}
          moveSection={moveProtokollSection}
          confirmDeleteSection={confirmDeleteProtokollSection}
          loadBefundbeispiel={loadBefundbeispiel}
          moveTemplate={moveProtokoll}
          confirmDeleteTemplate={confirmDeleteProtokoll}
          onSavePreviewTemplate={saveProtokollInline}
          onRenameBefundSection={renameProtokollSection}
          onDeleteBefundSection={deleteProtokollSection}
          showStatus={showStatus}
          Btn={Btn}
        />
      )}

      {activeTab === "freigaben" && isAdmin && (
        <AccessRequestsTab
          c={themeColors}
          dark={isDarkTheme}
          requests={accessRequests}
          loading={isAccessRequestsLoading}
          onRefresh={loadAccessRequests}
          onApprove={(entry) => updateAccessRequestStatus(entry, "approved")}
          onReject={(entry) => updateAccessRequestStatus(entry, "rejected")}
          onResend={resendAccessApprovedMail}
        />
      )}

      {showOnboarding && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: 18 }}>
          <div style={{ width: "100%", maxWidth: 520, background: themeColors.bgSecondary, border: `1px solid ${themeColors.border}`, borderRadius: 12, padding: 18, boxSizing: "border-box" }}>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Willkommen bei RadVentura</div>
            <div style={{ fontSize: 13, color: themeColors.textSecondary, lineHeight: 1.65 }}>
              Ihr Zugang wurde freigegeben. Sie können jetzt mit Befundung, Vorlagen und Befundbeispielen starten.
            </div>
            <div style={{ marginTop: 14, fontSize: 13, color: themeColors.textSecondary, lineHeight: 1.65 }}>
              Tipp: Beginnen Sie im Tab "Befundung" oder passen Sie zuerst Ihre Vorlagen im Tab "Vorlagen" an.
            </div>
            <div style={{ marginTop: 18, display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={() => {
                  if (sessionUserId && supabase) {
                    void saveUserPrivateData(USER_DATA_KEY_ONBOARDING_SEEN, true);
                  }
                  setShowOnboarding(false);
                }}
                style={{ background: isDarkTheme ? "#0d2d1a" : "#f0fff4", color: isDarkTheme ? "#48bb78" : "#276749", border: `1px solid ${isDarkTheme ? "#48bb78" : "#9ae6b4"}`, borderRadius: 8, cursor: "pointer", padding: "8px 14px", fontSize: 13, fontWeight: 600 }}
              >
                Los geht's
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === "vorlagen" && (
        <VorlagenTab
          c={themeColors}
          dark={isDarkTheme}
          lbl={fieldLabelStyle}
          inp={inputFieldStyle}
          vorlagenFilterText={vorlagenFilterText}
          setVorlagenFilterText={setVorlagenFilterText}
          newPromptFormOpen={newPromptFormOpen}
          setNewPromptFormOpen={setNewPromptFormOpen}
          newPromptMod={newPromptMod}
          setNewPromptMod={setNewPromptMod}
          newPromptMrtTarget={newPromptMrtTarget}
          setNewPromptMrtTarget={setNewPromptMrtTarget}
          vorlagenGroupOptions={visibleVorlagenGroups}
          vorlagenCreateGroupOptions={availableVorlagenCreateGroups}
          newVorlagenGroupName={newVorlagenGroupName}
          setNewVorlagenGroupName={setNewVorlagenGroupName}
          onCreateVorlagenGroup={createVorlagenGroup}
          onMoveVorlagenGroup={moveVorlagenGroup}
          onDeleteVorlagenGroup={deleteVorlagenGroup}
          newPromptSubsection={newPromptSubsection}
          setNewPromptSubsection={setNewPromptSubsection}
          newPromptCategory={newPromptCategory}
          setNewPromptCategory={setNewPromptCategory}
          newPromptName={newPromptName}
          setNewPromptName={setNewPromptName}
          newPromptBefund={newPromptBefund}
          setNewPromptBefund={setNewPromptBefund}
          newPromptBeurteilung={newPromptBeurteilung}
          setNewPromptBeurteilung={setNewPromptBeurteilung}
          onSaveNewPrompt={doSaveNewPrompt}
          isSavingNewPrompt={isSavingNewPrompt}
          vorlagenGruppiert={vorlagenGruppiert}
          expandedVorlagenGroups={expandedVorlagenGroups}
          toggleVorlagenGroup={toggleVorlagenGroup}
          filterVorlagen={filterVorlagen}
          loadVorlageToEditor={loadVorlageToEditor}
          onSavePreviewPrompt={saveVorlageInline}
          onDeletePrompt={(id) => { if (!requireAuth()) return; setPromptDeleteConfirm({ show: true, id }); }}
          onRenameSubsection={renameVorlagenSubsection}
          onDeleteSubsection={deleteVorlagenSubsection}
          subsectionOrderByMod={vorlagenSubsectionOrderByMod}
          onMoveSubsection={moveVorlagenSubsection}
          onRenameSubsectionOrder={renameVorlagenSubsectionOrder}
          onDeleteSubsectionOrder={deleteVorlagenSubsectionOrder}
          categoryById={vorlagenCategoryById}
          onChangeCategory={updateVorlageCategory}
          emptyCategoriesBySubsection={vorlagenEmptyCategoriesBySubsection}
          onAddEmptyCategory={addVorlagenEmptyCategory}
          modBadge={modBadge}
          modColor={modColor}
          showStatus={showStatus}
          Btn={Btn}
        />
      )}

      {activeTab === "bibliothek" && (
        <ResourcesTab
          c={themeColors}
          lbl={fieldLabelStyle}
          inp={inputFieldStyle}
          resources={externalResources}
          libraryLoadInfo={libraryLoadInfo}
          newResourceName={newResourceName}
          setNewResourceName={setNewResourceName}
          newResourceUrl={newResourceUrl}
          setNewResourceUrl={setNewResourceUrl}
            onCreateResource={() => { if (!requireAuth()) return; createExternalResource(); }}
          editingResource={editingResource}
          setEditingResource={setEditingResource}
            onUpdateResource={updateExternalResource}
            onDeleteResource={deleteExternalResource}
            onMoveResource={moveExternalResource}
            onReorderResource={reorderExternalResource}
        />
      )}

      <CodeConfirmModal
        open={deleteConfirm.show}
        c={themeColors}
        inp={inputFieldStyle}
        title="Befundbeispiel löschen bestätigen"
        message="Möchten Sie dieses Befundbeispiel wirklich löschen?"
        onConfirm={doDeleteTemplate}
        onCancel={() => setDeleteConfirm({ show: false, id: null, modGroup: null, section: null })}
        confirmLabel="Löschen"
      />

      <CodeConfirmModal
        open={sectionDeleteConfirm.show}
        c={themeColors}
        inp={inputFieldStyle}
        title="Sektion löschen bestätigen"
        message="Möchten Sie die Sektion und alle Befundbeispiele wirklich löschen?"
        onConfirm={doDeleteSection}
        onCancel={() => setSectionDeleteConfirm({ show: false, modGroup: null, section: null })}
        confirmLabel="Löschen"
      />

      <CodeConfirmModal
        open={promptDeleteConfirm.show}
        c={themeColors}
        inp={inputFieldStyle}
        title="Vorlage löschen bestätigen"
        message="Möchten Sie diese Vorlage wirklich löschen?"
        onConfirm={doDeletePrompt}
        onCancel={() => setPromptDeleteConfirm({ show: false, id: null })}
        confirmLabel="Löschen"
      />

    </div>
  );
}
